import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
  Search as SearchIcon, Upload, Plus, Trash2, ExternalLink, MessageCircle,
  Mail, Phone, Globe, Linkedin, UserPlus, Pencil, X, Check, Loader2,
  Sparkles, Download, ChevronUp, ChevronDown, MessageSquare, ListChecks,
  AlertTriangle, FileText, LayoutGrid, Table as TableIcon, Send, FileCheck, Crown,
  Clock, Target as TargetIcon, TrendingUp, Flame, Star, Sparkle,
} from 'lucide-react';
import {
  listProspects, getProspectFilters, getProspectStats,
  createProspect, updateProspect, deleteProspect,
  bulkProspectAction, deleteAllProspects, enrichProspect,
  autoClassifyProspectTiers, autoAssignProspectMarkets,
  listEmailTemplates, createEmailTemplate,
  bulkEnrichProspects, getBulkEnrichStatus,
  getProspectEmailTemplate, sendEmailToProspect, listSenderProfiles,
  bulkSendEmail, getBulkSendStatus, getProspectEmailAnalytics, cancelBulkSendJob,
  resetProspectEmailAnalytics,
  addProspectNote, getProspectActivity, getProspectActivityTimeline, exportProspects,
  convertProspectToSubscriber,
  markWaSent, markQuoteSent, markCustomer,
  getProspectById,
} from '../../lib/campaignsApi';
import { convertProspectToBuyer, readApiError } from '../../lib/api';
import { ProspectImportModal } from './ProspectImportModal';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

// ─── Status colour palette ────────────────────────────────────────────
const STATUS_COLORS = {
  'Not Contacted':  'bg-white/10 text-white/70',
  'Email Sent':     'bg-blue-400/15 text-blue-200',
  'WhatsApp Sent':  'bg-green-400/15 text-green-200',
  'Follow Up 1':    'bg-indigo-400/15 text-indigo-200',
  'Follow Up 2':    'bg-violet-400/15 text-violet-200',
  'Interested':     'bg-emerald-400/15 text-emerald-200',
  'Quote Sent':     'bg-teal-400/15 text-teal-200',
  'Negotiating':    'bg-[#00D4FF]/20 text-[#00D4FF]',
  'Customer':       'bg-amber-300/20 text-amber-100',
};

const EMPTY = {
  company_name: '', country: '', city: '', website: '', company_type: '',
  contact_page_url: '', public_email: '', public_phone: '', whatsapp: '',
  linkedin_url: '', brands_sold: '', notes: '', outreach_status: 'Not Contacted',
  contact_name: '', contact_title: '', contact_email: '', contact_email_verified: false,
  linkedin_person_url: '',
  buyer_potential_tier: 'B', estimated_annual_units: '', recommended_action: 'Email Campaign',
  territory: '',
  assigned_owner: 'Ernesto',
};

const STATUSES_ORDERED = [
  'Not Contacted', 'Email Sent', 'WhatsApp Sent',
  'Follow Up 1', 'Follow Up 2',
  'Interested', 'Quote Sent', 'Negotiating', 'Customer',
];

// ─── Buyer Potential Tier ────────────────────────────────────────────
const BUYER_TIERS = ['A+', 'A', 'B', 'C', 'D'];
const TIER_LABELS = {
  'A+': 'Strategic Buyer',
  'A':  'Large Buyer',
  'B':  'Medium Buyer',
  'C':  'Small Buyer',
  'D':  'Low Priority',
};
const TIER_RECOMMENDED_ACTION = {
  'A+': 'Call + Email + LinkedIn',
  'A':  'Email + Call',
  'B':  'Email Campaign',
  'C':  'Automated Nurture',
  'D':  'Archive / Low Priority',
};
const TIER_COLORS = {
  'A+': 'bg-[#00D4FF] text-black font-bold',
  'A':  'bg-emerald-500/30 text-emerald-100 font-semibold',
  'B':  'bg-amber-500/25 text-amber-100',
  'C':  'bg-white/15 text-white/70',
  'D':  'bg-white/[0.04] text-white/40',
};
const TierBadge = ({ tier }) => {
  const t = BUYER_TIERS.includes(tier) ? tier : 'B';
  return (
    <span
      className={`inline-block text-[11px] tracking-wide px-1.5 py-0.5 min-w-[28px] text-center ${TIER_COLORS[t]}`}
      title={`${t} · ${TIER_LABELS[t]}`}
      data-testid={`tier-badge-${t}`}
    >
      {t}
    </span>
  );
};

// ─── Territory (campaign segmentation) ───────────────────────────────
const TERRITORIES = ['Mexico', 'Central America', 'South America', 'Caribbean', 'Multi-Country'];

// ─── Canonical country list (kept in sync with backend ALLOWED_COUNTRIES) ─
const ALLOWED_COUNTRIES = [
  'Argentina', 'Bahamas', 'Barbados', 'Belize', 'Bolivia', 'Brazil', 'Chile',
  'Colombia', 'Costa Rica', 'Dominican Republic', 'Ecuador', 'El Salvador',
  'Guatemala', 'Guyana', 'Honduras', 'Jamaica', 'Mexico', 'Nicaragua',
  'Panama', 'Paraguay', 'Peru', 'Puerto Rico', 'Suriname',
  'Trinidad and Tobago', 'Uruguay', 'Venezuela',
  'Multi-Country', 'Unknown',
];

const DEFAULT_OWNER = 'Ernesto';
// Hard-coded owner list (small static set today; expandable later).
const OWNERS = ['Ernesto'];

// "2d ago" formatter for the Last Activity column.
const _activityRelative = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};
const LastActivityCell = ({ value }) => {
  if (!value) return <span className="text-white/30 text-[11px]">Never contacted</span>;
  const [label, ts] = value.split('|');
  return (
    <span className="text-[11px] text-white/65 leading-tight" title={ts || ''}>
      {label}
      {ts && <span className="text-white/35"> · {_activityRelative(ts)}</span>}
    </span>
  );
};

// ─── Small helpers ────────────────────────────────────────────────────
const Field = ({ label, children, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1">{label}</label>
    {children}
  </div>
);
const TextInput = (props) => (
  <input {...props} className={`w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm ${props.className || ''}`} />
);
const StatusBadge = ({ status }) => (
  <span className={`inline-block text-[10px] tracking-widest uppercase px-2 py-0.5 ${STATUS_COLORS[status] || 'bg-white/10 text-white/70'}`}>
    {status}
  </span>
);
const PriorityBadge = ({ score = 0 }) => {
  let bg = 'bg-white/10 text-white/55';
  if (score >= 80) bg = 'bg-emerald-500/25 text-emerald-200';
  else if (score >= 60) bg = 'bg-teal-500/20 text-teal-200';
  else if (score >= 40) bg = 'bg-amber-500/20 text-amber-200';
  else if (score >= 20) bg = 'bg-white/15 text-white/65';
  return (
    <span className={`inline-block text-[11px] font-bold px-1.5 py-0.5 min-w-[32px] text-center ${bg}`} title="Priority Score 0-100">
      {score}
    </span>
  );
};
const buildWaUrl = (p) => {
  const raw = (p.whatsapp || p.public_phone || '').trim();
  if (!raw) return null;
  // Reduce to digits only for wa.me
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits || digits.length < 7) return null;
  // Aligned with the default Spanish first-touch email template — same inventory
  // bullets, same call-to-action, same Luxor Tek positioning. WhatsApp doesn't
  // render markdown bullets reliably, so we use a unicode bullet on each line.
  const msg = encodeURIComponent(
    `Hola,\n\n` +
    `Soy Ernesto Bustillo de Luxor Tek en Miami, Florida.\n\n` +
    `Suministramos laptops, desktops y mini PCs reacondicionados para distribuidores y mayoristas en Latinoamérica.\n\n` +
    `Inventario disponible:\n\n` +
    `• Dell Latitude\n` +
    `• HP EliteBook\n` +
    `• Lenovo ThinkPad\n` +
    `• Desktops y Mini PCs\n` +
    `• Grados A y B\n` +
    `• Garantía de 90 días\n\n` +
    `Si le interesa, con gusto puedo enviar nuestra lista actual de inventario y precios en USD.\n\n` +
    `Más información:\n` +
    `https://luxortek.com`
  );
  return `https://wa.me/${digits}?text=${msg}`;
};

// Pull a clean, human-friendly phone out of a raw whatsapp/phone field that may
// contain a wa.me URL, an api.whatsapp.com URL, or already-formatted digits.
const formatPhoneDisplay = (raw) => {
  if (!raw) return '';
  let s = String(raw).trim();
  // Extract digits from a wa.me / api.whatsapp.com / tel: URL
  const urlMatch = s.match(/(?:wa\.me\/|api\.whatsapp\.com\/send\?phone=|tel:)\+?(\d[\d\s().\-]*)/i);
  if (urlMatch) s = urlMatch[1];
  // Keep only digits, then group from the right: cc + national segments
  const digits = s.replace(/[^0-9]/g, '');
  if (digits.length < 7) return s; // unrecognised — return as-is
  // 10 digits → "ddd ddd dddd"; 11 → "+d ddd ddd dddd"; 12 → "+dd ddd ddd dddd"; 13 → "+dd ddd ddd dddd d"
  if (digits.length === 10) return `${digits.slice(0,3)} ${digits.slice(3,6)} ${digits.slice(6)}`;
  if (digits.length === 11) return `+${digits[0]} ${digits.slice(1,4)} ${digits.slice(4,7)} ${digits.slice(7)}`;
  if (digits.length === 12) return `+${digits.slice(0,2)} ${digits.slice(2,5)} ${digits.slice(5,8)} ${digits.slice(8)}`;
  if (digits.length === 13) return `+${digits.slice(0,2)} ${digits.slice(2,5)} ${digits.slice(5,8)} ${digits.slice(8,12)} ${digits.slice(12)}`;
  return `+${digits}`;
};


// ─── Edit modal with notes + activity ────────────────────────────────
const ProspectEditModal = ({ prospect, statuses, onClose, onSaved }) => {
  const isNew = !prospect?.id;
  const [form, setForm] = useState({ ...EMPTY, ...(prospect || {}) });
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const change = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  // Auto-fill recommended_action when tier changes — unless the user has
  // already manually edited the action away from any tier's default value.
  const changeTier = (newTier) => {
    setForm((s) => {
      const currentAction = (s.recommended_action || '').trim();
      const isStillAtPreviousDefault =
        !currentAction ||
        Object.values(TIER_RECOMMENDED_ACTION).includes(currentAction);
      return {
        ...s,
        buyer_potential_tier: newTier,
        recommended_action: isStillAtPreviousDefault
          ? TIER_RECOMMENDED_ACTION[newTier]
          : currentAction,
      };
    });
  };

  const loadActivity = useCallback(async () => {
    if (!prospect?.id) return;
    try {
      const r = await getProspectActivity(prospect.id);
      setActivity(r.data.activity || []);
    } catch (e) { /* silent */ }
    // Phase 5 · Activity Timeline (email_messages + email_events + RFQ/quote)
    try {
      const t = await getProspectActivityTimeline(prospect.id);
      setTimeline(t.data.items || []);
    } catch (e) { /* silent */ }
  }, [prospect?.id]);
  useEffect(() => { loadActivity(); }, [loadActivity]);

  const submit = async () => {
    try {
      setSaving(true);
      // Coerce empty string → null for numeric fields so Pydantic accepts the payload.
      const payload = {
        ...form,
        estimated_annual_units:
          form.estimated_annual_units === '' || form.estimated_annual_units == null
            ? null
            : Number(form.estimated_annual_units),
      };
      if (isNew) await createProspect(payload);
      else await updateProspect(prospect.id, payload);
      toast.success(isNew ? 'Prospect created' : 'Prospect updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const submitNote = async () => {
    if (!newNote.trim()) return;
    try {
      setAddingNote(true);
      await addProspectNote(prospect.id, newNote.trim());
      setNewNote(''); loadActivity();
      toast.success('Note added');
    } catch (e) { toast.error('Note failed'); }
    finally { setAddingNote(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="prospect-edit-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-[820px] max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
          <div className="text-white font-semibold flex items-center gap-2">
            {isNew ? 'New Prospect' : (
              <>
                <span>{prospect.company_name}</span>
                <PriorityBadge score={prospect.priority_score} />
              </>
            )}
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Company Name *"><TextInput value={form.company_name} onChange={(e) => change('company_name', e.target.value)} data-testid="prospect-company-input" /></Field>
          <Field label="Country *"><TextInput value={form.country} onChange={(e) => change('country', e.target.value)} data-testid="prospect-country-input" /></Field>
          <Field label="City"><TextInput value={form.city} onChange={(e) => change('city', e.target.value)} /></Field>
          <Field label="Website"><TextInput value={form.website} onChange={(e) => change('website', e.target.value)} placeholder="https://" /></Field>
          <Field label="Company Type"><TextInput value={form.company_type} onChange={(e) => change('company_type', e.target.value)} /></Field>
          <Field label="Contact Page URL"><TextInput value={form.contact_page_url} onChange={(e) => change('contact_page_url', e.target.value)} /></Field>
          <Field label="Public Email"><TextInput type="email" value={form.public_email} onChange={(e) => change('public_email', e.target.value)} /></Field>
          <Field label="Public Phone"><TextInput value={form.public_phone} onChange={(e) => change('public_phone', e.target.value)} /></Field>
          <Field label="WhatsApp"><TextInput value={form.whatsapp} onChange={(e) => change('whatsapp', e.target.value)} placeholder="+56 9 7483 5654" /></Field>
          <Field label="LinkedIn Company URL"><TextInput value={form.linkedin_url} onChange={(e) => change('linkedin_url', e.target.value)} /></Field>
          <Field label="Brands Sold"><TextInput value={form.brands_sold} onChange={(e) => change('brands_sold', e.target.value)} placeholder="HP Lenovo Dell" /></Field>
          <Field label="Outreach Status">
            <select value={form.outreach_status} onChange={(e) => change('outreach_status', e.target.value)} className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none" data-testid="prospect-status-select">
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>

          <div className="md:col-span-2 border-t border-white/5 pt-3 mt-2">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-[#00D4FF] mb-2">Buyer Potential</div>
          </div>
          <Field label="Buyer Potential Tier">
            <select
              value={form.buyer_potential_tier || 'B'}
              onChange={(e) => changeTier(e.target.value)}
              className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
              data-testid="prospect-tier-select"
            >
              {BUYER_TIERS.map((t) => (
                <option key={t} value={t}>{t} — {TIER_LABELS[t]}</option>
              ))}
            </select>
          </Field>
          <Field label="Estimated Annual Units">
            <TextInput
              type="number"
              min="0"
              value={form.estimated_annual_units ?? ''}
              onChange={(e) => change('estimated_annual_units', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              placeholder="e.g. 500"
              data-testid="prospect-units-input"
            />
          </Field>
          <Field label="Recommended Action" full>
            <TextInput
              value={form.recommended_action || ''}
              onChange={(e) => change('recommended_action', e.target.value)}
              placeholder="Auto-filled from tier; edit to override"
              data-testid="prospect-action-input"
            />
          </Field>
          <Field label="Market">
            <select
              value={form.territory || ''}
              onChange={(e) => change('territory', e.target.value)}
              className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
              data-testid="prospect-territory-select"
            >
              <option value="">— None —</option>
              {TERRITORIES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Assigned Owner">
            <select
              value={form.assigned_owner || DEFAULT_OWNER}
              onChange={(e) => change('assigned_owner', e.target.value)}
              className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
              data-testid="prospect-owner-select"
            >
              {OWNERS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>

          <div className="md:col-span-2 border-t border-white/5 pt-3 mt-2">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-[#00D4FF] mb-2">Decision-Maker (manual research / future Hunter.io)</div>
          </div>
          <Field label="Contact Name"><TextInput value={form.contact_name} onChange={(e) => change('contact_name', e.target.value)} placeholder="e.g. Juan García" /></Field>
          <Field label="Contact Title"><TextInput value={form.contact_title} onChange={(e) => change('contact_title', e.target.value)} placeholder="e.g. Gerente de Compras" /></Field>
          <Field label="Contact Email"><TextInput type="email" value={form.contact_email} onChange={(e) => change('contact_email', e.target.value)} /></Field>
          <Field label="LinkedIn Person URL"><TextInput value={form.linkedin_person_url} onChange={(e) => change('linkedin_person_url', e.target.value)} /></Field>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-white/75">
              <input type="checkbox" checked={!!form.contact_email_verified} onChange={(e) => change('contact_email_verified', e.target.checked)} />
              Contact email is verified
            </label>
          </div>
          <div className="md:col-span-2">
            <Field label="Notes (general)">
              <textarea value={form.notes} onChange={(e) => change('notes', e.target.value)} rows={2} className="w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm" />
            </Field>
          </div>
        </div>

        {!isNew && (
          <div className="px-5 pb-5 border-t border-white/5 pt-4">
            {/* Phase 5 · Activity Timeline (email_messages + email_events) */}
            {timeline.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2 flex items-center gap-2"><MessageSquare className="w-3 h-3" /> Activity Timeline</div>
                <div className="space-y-1.5 max-h-56 overflow-auto border border-white/5 bg-[#050505] p-2" data-testid="prospect-activity-timeline">
                  {timeline.map((t, i) => {
                    const KIND_COLOR = {
                      'email_sent': 'text-emerald-400',
                      'email.delivered': 'text-emerald-300',
                      'email.opened': 'text-cyan-300',
                      'email.clicked': 'text-[#00D4FF]',
                      'email.bounced': 'text-red-400',
                      'email.complained': 'text-red-400',
                      'rfq_created': 'text-amber-300',
                      'quote_created': 'text-violet-300',
                    };
                    const KIND_LABEL = {
                      'email_sent': 'Email Sent',
                      'email.delivered': 'Delivered',
                      'email.opened': 'Opened',
                      'email.clicked': 'Clicked',
                      'email.bounced': 'Bounced',
                      'email.complained': 'Complained',
                      'rfq_created': 'RFQ',
                      'quote_created': 'Quote',
                    };
                    return (
                      <div key={`tl-${i}`} className="border-l-2 border-white/10 px-2 py-1 text-xs" data-testid={`timeline-item-${i}`}>
                        <div className="flex items-center gap-2 text-white/45 text-[11px]">
                          <span>{t.at ? new Date(t.at).toLocaleString() : '—'}</span>
                          <span className={`uppercase tracking-widest text-[9px] ${KIND_COLOR[t.kind] || 'text-white/55'}`}>
                            {KIND_LABEL[t.kind] || t.kind}
                          </span>
                          {t.source && <span className="text-white/30 text-[9px]">· {t.source}</span>}
                          {t.language && <span className="text-white/30 text-[9px]">· {t.language}</span>}
                        </div>
                        {t.subject && <div className="text-white/75 mt-0.5 truncate">{t.subject}</div>}
                        {t.url && <div className="text-[#00D4FF]/80 mt-0.5 truncate text-[11px]">→ {t.url}</div>}
                        {t.kind === 'rfq_created' && (
                          <div className="text-white/55 mt-0.5">
                            {[t.brand, t.model].filter(Boolean).join(' ')}
                            {t.quantity ? ` · qty ${t.quantity}` : ''}
                            {t.status ? ` · ${t.status}` : ''}
                          </div>
                        )}
                        {t.kind === 'quote_created' && (
                          <div className="text-white/55 mt-0.5">
                            #{t.quote_id} · {t.items_count || 0} item(s)
                            {t.status ? ` · ${t.status}` : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2 flex items-center gap-2"><MessageSquare className="w-3 h-3" /> Activity & Notes</div>
            <div className="flex gap-2 mb-3">
              <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitNote()}
                placeholder="Add a note (e.g. 'Called Marco, interested in mixed pallets')…"
                className="flex-1 px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm" />
              <button onClick={submitNote} disabled={!newNote.trim() || addingNote} className="btn-secondary text-sm inline-flex items-center gap-1 disabled:opacity-40">
                {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Add
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-auto border border-white/5 bg-[#050505] p-2">
              {activity.length === 0 ? (
                <div className="text-white/40 text-xs p-2">No activity yet.</div>
              ) : (
                [...activity].reverse().map((a) => (
                  <div key={a.id} className="border-l-2 border-white/10 px-2 py-1.5 text-xs">
                    <div className="flex items-center gap-2 text-white/45 text-[11px]">
                      <span>{new Date(a.at).toLocaleString()}</span>
                      <span className="text-[#00D4FF] uppercase tracking-widest text-[9px]">{a.action.replace('_', ' ')}</span>
                      {a.by && <span className="text-white/35">· {a.by}</span>}
                    </div>
                    {a.action === 'note' && <div className="text-white/80 mt-0.5">{a.payload?.text}</div>}
                    {a.action === 'status_change' && <div className="text-white/65 mt-0.5">{a.payload?.from || '—'} → <span className="text-white">{a.payload?.to}</span></div>}
                    {a.action === 'email_sent' && <div className="text-white/65 mt-0.5">Marked email sent · status → {a.payload?.new_status}</div>}
                    {a.action === 'whatsapp_sent' && <div className="text-white/65 mt-0.5">Marked WhatsApp sent · status → {a.payload?.new_status}</div>}
                    {a.action === 'quote_sent' && <div className="text-white/65 mt-0.5">Quote sent</div>}
                    {a.action === 'customer_won' && <div className="text-amber-200 mt-0.5">Customer won 🎉</div>}
                    {a.action === 'enriched' && <div className="text-white/65 mt-0.5">{a.payload?.no_new_data ? 'No new data found' : `Filled: ${(a.payload?.filled || []).join(', ')}`}</div>}
                    {a.action === 'created' && <div className="text-white/55 mt-0.5">Prospect created{a.payload?.via ? ` via ${a.payload.via}` : ''}</div>}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2 sticky bottom-0 bg-[#0A0A0A]">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={!form.company_name || !form.country || saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="prospect-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}{isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete All modal (replaced with DangerConfirmModal) ─────────────
// Kept as a thin wrapper that calls the API with the confirmed phrase.
// The DangerConfirmModal component handles the typed-phrase + acknowledgment UI.

// ─── Bulk Enrich modal (confirmation + live progress) ─────────────────
const BulkEnrichModal = ({ mode, ids, totalLabel, onClose, onDone }) => {
  // Phases: 'confirm' → 'running' → 'done' → 'failed'
  const [phase, setPhase] = useState('confirm');
  const [jobId, setJobId] = useState(null);
  const [state, setState] = useState({ total: 0, completed: 0, succeeded: 0, no_data: 0, failed_count: 0, results: [] });

  const start = async () => {
    try {
      setPhase('running');
      const body = mode ? { mode } : { ids };
      const { data } = await bulkEnrichProspects(body);
      setJobId(data.job_id);
      setState((s) => ({ ...s, total: data.total }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start bulk enrichment');
      setPhase('failed');
    }
  };

  // Poll while running
  useEffect(() => {
    if (phase !== 'running' || !jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await getBulkEnrichStatus(jobId);
        if (cancelled) return;
        setState(data);
        if (data.status === 'done') {
          setPhase('done');
          onDone && onDone();
        } else if (data.status === 'failed') {
          setPhase('failed');
        }
      } catch (e) {
        if (!cancelled) toast.error('Lost connection to enrichment job');
      }
    };
    tick();
    const iv = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [phase, jobId, onDone]);

  const pct = state.total ? Math.round((state.completed / state.total) * 100) : 0;
  const targetText = mode === 'not_contacted_missing_info'
    ? 'all "Not Contacted" prospects that are missing email / phone / WhatsApp / LinkedIn'
    : `${ids?.length || 0} selected prospect${(ids?.length || 0) === 1 ? '' : 's'}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={phase === 'running' ? undefined : onClose} data-testid="bulk-enrich-modal">
      <div className="bg-[#0A0A0A] border border-violet-500/40 w-full max-w-[640px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/30">
          <div className="text-violet-200 font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            {phase === 'confirm' ? 'Bulk Enrich Prospects' : phase === 'running' ? 'Enriching…' : phase === 'done' ? 'Enrichment Complete' : 'Enrichment Failed'}
          </div>
          {phase !== 'running' && (
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="bulk-enrich-close-btn">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {phase === 'confirm' && (
          <>
            <div className="p-5 space-y-3 text-sm">
              <div className="text-white/85">
                This will scan {totalLabel || targetText} and fill in missing email / phone / WhatsApp / LinkedIn / contact page from each website.
              </div>
              <div className="text-white/55 text-xs space-y-1">
                <div>• Manually-entered fields are never overwritten — only empty fields get filled.</div>
                <div>• Processed in batches of 5 concurrent fetches, ~12s timeout per site.</div>
                <div>• You can keep this modal open to watch progress live.</div>
                <div className="text-amber-300/85">⚠ This may take a few minutes depending on website response times.</div>
              </div>
            </div>
            <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2">
              <button onClick={onClose} className="btn-secondary text-sm" data-testid="bulk-enrich-cancel-btn">Cancel</button>
              <button onClick={start}
                      className="px-4 py-2 text-sm bg-violet-500 text-white hover:bg-violet-600 inline-flex items-center gap-2"
                      data-testid="bulk-enrich-confirm-btn">
                <Sparkles className="w-4 h-4" />Start enrichment
              </button>
            </div>
          </>
        )}

        {(phase === 'running' || phase === 'done' || phase === 'failed') && (
          <div className="p-5 space-y-4 text-sm">
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs text-white/65 mb-1">
                <span>Progress</span>
                <span data-testid="bulk-enrich-progress-label">{state.completed} / {state.total} ({pct}%)</span>
              </div>
              <div className="w-full h-2 bg-white/10 overflow-hidden">
                <div className="h-full bg-violet-400 transition-all duration-300" style={{ width: `${pct}%` }} data-testid="bulk-enrich-progress-bar" />
              </div>
            </div>

            {/* Counters */}
            <div className="grid grid-cols-4 gap-2">
              <Counter label="Completed" value={state.completed} color="text-white" testId="bulk-enrich-completed" />
              <Counter label="Successful" value={state.succeeded} color="text-emerald-300" testId="bulk-enrich-succeeded" />
              <Counter label="No data" value={state.no_data} color="text-amber-300" testId="bulk-enrich-no-data" />
              <Counter label="Failed" value={state.failed_count} color="text-red-300" testId="bulk-enrich-failed" />
            </div>

            {/* Latest results (tail) */}
            {(state.results && state.results.length > 0) && (
              <div className="border border-white/10 max-h-[220px] overflow-y-auto bg-black/40">
                {state.results.slice(-20).reverse().map((r) => (
                  <div key={r.prospect_id} className="px-3 py-1.5 text-xs border-b border-white/5 flex items-center gap-2">
                    {r.status === 'ok' && <Check className="w-3.5 h-3.5 text-emerald-300 shrink-0" />}
                    {r.status === 'no_data' && <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />}
                    {r.status === 'failed' && <X className="w-3.5 h-3.5 text-red-300 shrink-0" />}
                    <span className="text-white/85 truncate flex-1">{r.company_name || r.prospect_id}</span>
                    <span className="text-white/55 truncate">
                      {r.status === 'ok' && (r.filled_fields || []).join(', ')}
                      {r.status === 'no_data' && 'no new data'}
                      {r.status === 'failed' && (r.error || 'failed')}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-white/55 flex items-center gap-2">
              {phase === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />}
              {phase === 'running' && 'Running… you can close this and re-open the page; the job continues in the background.'}
              {phase === 'done' && <span className="text-emerald-300">Job finished — table refreshed.</span>}
              {phase === 'failed' && <span className="text-red-300">Job failed.</span>}
            </div>

            {phase !== 'running' && (
              <div className="flex justify-end">
                <button onClick={onClose} className="btn-primary text-sm" data-testid="bulk-enrich-done-btn">Close</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Counter = ({ label, value, color, testId }) => (
  <div className="bg-white/5 border border-white/10 p-2.5" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-white/45">{label}</div>
    <div className={`text-xl font-bold ${color}`}>{value}</div>
  </div>
);


// ─── D1.6 · Smart template badge above the editor ─────────────────────
const TEMPLATE_BADGE_META = {
  rfq_followup: {
    cls: 'bg-red-500/10 border-red-500/40 text-red-300',
    Icon: Flame,
    blurb: (ctx) => `Tied to sourcing request${ctx?.rfq_item ? ` · ${ctx.rfq_item}` : ''}`,
  },
  quote_followup: {
    cls: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
    Icon: FileText,
    blurb: (ctx) => `Tied to quote ${ctx?.quote_id || ''}`.trim(),
  },
  customer_followup: {
    cls: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    Icon: Star,
    blurb: (ctx) => `Existing customer${ctx?.customer_company ? ` · ${ctx.customer_company}` : ''}`,
  },
  // Phase 5.3 — single generic engagement follow-up
  campaign_followup: {
    cls: 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300',
    Icon: Flame,
    blurb: () => 'Recent campaign engagement',
  },
  cold_prospect: {
    cls: 'bg-white/[0.03] border-white/15 text-white/65',
    Icon: Sparkle,
    blurb: () => 'No prior history — first-touch introduction template',
  },
};

const TemplateBadge = ({ kind, label, ctx }) => {
  const meta = TEMPLATE_BADGE_META[kind] || TEMPLATE_BADGE_META.cold_prospect;
  const Icon = meta.Icon;
  return (
    <div
      data-testid="smart-template-badge"
      data-template-kind={kind}
      className={`flex items-start gap-3 px-3 py-2.5 border ${meta.cls}`}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold tracking-widest uppercase opacity-70">
          Smart template
        </div>
        <div className="text-sm font-semibold" data-testid="smart-template-label">{label}</div>
        <div className="text-xs opacity-75 mt-0.5">{meta.blurb(ctx)}</div>
      </div>
    </div>
  );
};


// ─── D1.7 · WYSIWYG rich editor for email composer ────────────────────
// A small contentEditable wrapper that:
//   • initialises from `html` once and whenever `html` changes from an
//     external source (e.g. a template reload or HTML-mode edit).
//   • emits `onChange(newHtml)` on input/blur WITHOUT triggering a
//     re-render that would reset the cursor.
// Pattern: track the last value we emitted in a ref and only re-set
// innerHTML when the incoming prop diverges from it.
const RichEditor = ({ html, onChange, testid }) => {
  const ref = React.useRef(null);
  const lastEmitted = React.useRef(null);

  React.useEffect(() => {
    if (!ref.current) return;
    if (html !== lastEmitted.current) {
      ref.current.innerHTML = html || '';
      lastEmitted.current = html || '';
    }
  }, [html]);

  const emit = () => {
    if (!ref.current) return;
    const v = ref.current.innerHTML;
    lastEmitted.current = v;
    onChange(v);
  };

  const exec = (cmd, val = null) => {
    // Native rich-text commands. Deprecated in spec but still the
    // pragmatic browser support for contentEditable formatting.
    document.execCommand(cmd, false, val);
    emit();
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL (https://…)');
    if (!url) return;
    exec('createLink', url);
  };

  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5" data-testid="rich-editor-toolbar">
        <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec('bold')}
                title="Bold (Cmd/Ctrl+B)"
                data-testid="rt-bold"
                className="px-2 py-1 text-xs border border-white/10 text-white/70 hover:border-[#00D4FF] hover:text-[#00D4FF]">
          <strong>B</strong>
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec('italic')}
                title="Italic (Cmd/Ctrl+I)"
                data-testid="rt-italic"
                className="px-2 py-1 text-xs border border-white/10 text-white/70 hover:border-[#00D4FF] hover:text-[#00D4FF]">
          <em>I</em>
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec('insertUnorderedList')}
                title="Bullet list"
                data-testid="rt-bullet"
                className="px-2 py-1 text-xs border border-white/10 text-white/70 hover:border-[#00D4FF] hover:text-[#00D4FF]">
          • List
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={insertLink}
                title="Insert link"
                data-testid="rt-link"
                className="px-2 py-1 text-xs border border-white/10 text-white/70 hover:border-[#00D4FF] hover:text-[#00D4FF]">
          ⛓ Link
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec('removeFormat')}
                title="Clear formatting"
                data-testid="rt-clear"
                className="px-2 py-1 text-xs border border-white/10 text-white/55 hover:border-white/30 hover:text-white/80 ml-auto">
          Clear
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={testid}
        className="border border-white/10 bg-white p-4 text-black text-sm min-h-[280px] max-h-[440px] overflow-y-auto focus:outline-none focus:border-[#00D4FF] leading-relaxed prose prose-sm max-w-none"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      />
    </div>
  );
};


// ─── Send Email to Prospect modal ─────────────────────────────────────
const SendProspectEmailModal = ({ prospect, onClose, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [senderProfileId, setSenderProfileId] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  // D1.7 — editor mode: 'compose' (default WYSIWYG) | 'preview' | 'html'
  const [editorMode, setEditorMode] = useState('compose');
  // D1.6 — smart template badge
  const [templateKind, setTemplateKind] = useState('cold_prospect');
  const [templateLabel, setTemplateLabel] = useState('Cold Prospect');
  const [templateContext, setTemplateContext] = useState({});

  const [loadError, setLoadError] = useState(null);

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [tplRes, profRes] = await Promise.all([
        getProspectEmailTemplate(prospect.id),
        listSenderProfiles(),
      ]);
      const t = tplRes.data;
      const ps = (profRes.data || []).filter((p) => p.is_active);
      if (!ps.length) {
        throw new Error('No active sender profiles configured. Set one up in Admin → Sender Profiles.');
      }
      if (!t || !t.subject || !t.body_html) {
        throw new Error('Server returned an empty template. The /admin/prospects/email-template endpoint may not be deployed on this environment.');
      }
      setProfiles(ps);
      setSenderProfileId(t.default_sender_profile_id || ps[0].id);
      setToEmail(t.to_email || '');
      setSubject(t.subject);
      setBodyHtml(t.body_html);
      setTemplateKind(t.template_kind || 'cold_prospect');
      setTemplateLabel(t.template_label || 'Cold Prospect');
      setTemplateContext(t.template_context || {});
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || 'Unknown error';
      let msg;
      if (status === 404) {
        msg = `Endpoint not found (404). The new in-app email API is not deployed in this environment. Redeploy preview → production and try again.\n\nDetail: ${detail}`;
      } else if (status === 401 || status === 403) {
        msg = `Not authorized (${status}). Your admin session may have expired — log out and back in.\n\nDetail: ${detail}`;
      } else if (status >= 500) {
        msg = `Server error (${status}). The backend failed to build the email template. Check backend logs.\n\nDetail: ${detail}`;
      } else if (!status) {
        msg = `Network error reaching the backend. Check that the API is reachable from this domain.\n\nDetail: ${detail}`;
      } else {
        msg = `${status}: ${detail}`;
      }
      setLoadError(msg);
      toast.error('Could not load prospect email template');
    } finally {
      setLoading(false);
    }
  }, [prospect.id]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(toEmail.trim());
  const canSend = !loading && !sending && validEmail && subject.trim() && bodyHtml.trim() && senderProfileId;

  const send = async () => {
    try {
      setSending(true);
      const { data } = await sendEmailToProspect(prospect.id, {
        sender_profile_id: senderProfileId,
        subject: subject.trim(),
        body_html: bodyHtml,
        to_email: toEmail.trim(),
      });
      toast.success(`Email sent to ${toEmail.trim()} · prospect → Email Sent`);
      onSent && onSent(data.prospect);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email send failed');
    } finally {
      setSending(false);
    }
  };

  const selectedProfile = profiles.find((p) => p.id === senderProfileId);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={sending ? undefined : onClose} data-testid="send-prospect-email-modal">
      <div className="bg-[#0A0A0A] border border-[#00D4FF]/40 w-full max-w-[820px] max-h-[90vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#00D4FF]/30 shrink-0">
          <div className="text-[#00D4FF] font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Send Email · {prospect.company_name}
          </div>
          {!sending && (
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="send-email-close-btn">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-10 text-center text-white/55 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading template…</div>
        ) : loadError ? (
          <div className="p-6 space-y-4" data-testid="send-email-load-error">
            <div className="border border-red-500/40 bg-red-500/10 p-4 text-sm">
              <div className="flex items-start gap-2 text-red-200">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold mb-1.5">Unable to prepare the email</div>
                  <pre className="text-xs whitespace-pre-wrap font-sans text-red-100/90">{loadError}</pre>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={loadTemplate} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="send-email-retry-btn">
                <Loader2 className="w-4 h-4" />Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-4 text-sm">
            {/* D1.6 — Smart template badge */}
            <TemplateBadge kind={templateKind} label={templateLabel} ctx={templateContext} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="From (sender profile)">
                <select value={senderProfileId} onChange={(e) => setSenderProfileId(e.target.value)}
                  className="w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
                  data-testid="send-email-sender-select">
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name} &lt;{p.email}&gt;{p.is_default ? ' · default' : ''}
                    </option>
                  ))}
                </select>
                {selectedProfile && (
                  <div className="text-[10px] text-white/45 mt-1 truncate">{selectedProfile.purpose}</div>
                )}
              </Field>
              <Field label="To">
                <TextInput value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                  placeholder="recipient@example.com"
                  className={validEmail ? '' : 'border-amber-500/50'}
                  data-testid="send-email-to-input" />
                {!validEmail && toEmail && <div className="text-[10px] text-amber-300 mt-1">Not a valid email format</div>}
              </Field>
            </div>

            <Field label="Subject">
              <TextInput value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="send-email-subject-input" />
            </Field>

            <Field label="Body">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-white/45">
                  {editorMode === 'compose' && 'Edit directly — formatting is preserved'}
                  {editorMode === 'preview' && 'Read-only preview'}
                  {editorMode === 'html' && 'Advanced — raw HTML source'}
                </div>
                <div className="inline-flex border border-white/10" data-testid="send-email-mode-switch">
                  {[
                    { k: 'compose', label: 'Compose' },
                    { k: 'preview', label: 'Preview' },
                    { k: 'html', label: 'HTML' },
                  ].map((m) => (
                    <button
                      key={m.k}
                      type="button"
                      onClick={() => setEditorMode(m.k)}
                      data-testid={`send-email-mode-${m.k}`}
                      data-active={editorMode === m.k ? 'true' : 'false'}
                      className={`px-3 py-1 text-[10px] uppercase tracking-widest transition ${
                        editorMode === m.k
                          ? 'bg-[#00D4FF] text-black'
                          : 'text-white/55 hover:text-white'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {editorMode === 'compose' && (
                <RichEditor html={bodyHtml} onChange={setBodyHtml} testid="send-email-body-input" />
              )}
              {editorMode === 'preview' && (
                <div className="border border-white/10 bg-white p-4 text-black text-sm max-h-[440px] overflow-y-auto"
                  data-testid="send-email-body-preview"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              )}
              {editorMode === 'html' && (
                <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
                  rows={14}
                  className="w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-xs font-mono"
                  data-testid="send-email-body-html-input" />
              )}
              <div className="text-[10px] text-white/45 mt-1.5">
                Personalization tokens: <code className="text-[#00D4FF]">{`{first_name}`}</code>{' '}
                <code className="text-[#00D4FF]">{`{name}`}</code>{' '}
                <code className="text-[#00D4FF]">{`{company}`}</code>{' '}
                <code className="text-[#00D4FF]">{`{country}`}</code>{' '}
                <code className="text-[#00D4FF]">{`{city}`}</code>
              </div>
            </Field>

            <div className="text-[11px] text-white/50 bg-white/[0.02] border border-white/10 p-2.5">
              <div className="flex items-center gap-1.5 mb-1 text-white/70"><Sparkles className="w-3 h-3" />After sending</div>
              <div>• Prospect status will be set to <span className="text-blue-200">Email Sent</span>.</div>
              <div>• Open and click tracking are embedded — engagement appears on this prospect's Activity timeline.</div>
              <div>• A "Reply REMOVER to opt out" footer is appended automatically (CAN-SPAM).</div>
            </div>
          </div>
        )}

        <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={sending} className="btn-secondary text-sm" data-testid="send-email-cancel-btn">Cancel</button>
          <button onClick={send} disabled={!canSend}
            className="px-4 py-2 text-sm bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="send-email-submit-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};




// ─── Sort header ─────────────────────────────────────────────────────
const SortHeader = ({ label, field, sortBy, sortDir, onChange }) => {
  const active = sortBy === field;
  return (
    <th onClick={() => onChange(field, active && sortDir === 'asc' ? 'desc' : 'asc')}
        className={`text-left px-3 py-3 cursor-pointer select-none ${active ? 'text-[#00D4FF]' : ''} hover:text-white`}
        data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-1">{label}
        {active && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );
};

// ─── Action icon button (high-contrast, tinted pill + native tooltip) ─
const ACTION_COLOR_MAP = {
  blue:   'text-blue-200   bg-blue-500/15   border-blue-500/40   hover:bg-blue-500/30   hover:border-blue-400/70   hover:text-white',
  green:  'text-green-200  bg-green-500/15  border-green-500/40  hover:bg-green-500/30  hover:border-green-400/70  hover:text-white',
  teal:   'text-teal-200   bg-teal-500/15   border-teal-500/40   hover:bg-teal-500/30   hover:border-teal-400/70   hover:text-white',
  amber:  'text-amber-200  bg-amber-500/15  border-amber-500/40  hover:bg-amber-500/30  hover:border-amber-400/70  hover:text-white',
  violet: 'text-violet-200 bg-violet-500/15 border-violet-500/40 hover:bg-violet-500/30 hover:border-violet-400/70 hover:text-white',
  cyan:   'text-cyan-200   bg-cyan-500/15   border-cyan-500/40   hover:bg-cyan-500/30   hover:border-cyan-400/70   hover:text-white',
  red:    'text-red-200    bg-red-500/15    border-red-500/40    hover:bg-red-500/30    hover:border-red-400/70    hover:text-white',
};
const ActionIconBtn = ({ icon: Icon, tooltip, color = 'cyan', onClick, disabled, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={tooltip}
    aria-label={tooltip}
    data-testid={testId}
    className={`inline-flex items-center justify-center w-8 h-8 border transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-white/15 disabled:bg-transparent disabled:border-white/15 disabled:text-white/40 ${ACTION_COLOR_MAP[color]}`}
  >
    <Icon className="w-4 h-4" />
  </button>
);


// ─── Kanban card ─────────────────────────────────────────────────────
const KanbanCard = ({ p, onDragStart, onEdit }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, p.id)}
    onClick={() => onEdit(p)}
    className="bg-[#050505] border border-white/10 p-2.5 cursor-move hover:border-[#00D4FF]/30 mb-2 group"
    data-testid={`kanban-card-${p.id}`}
  >
    <div className="flex items-start justify-between gap-2 mb-1">
      <div className="text-white text-xs font-semibold truncate flex-1">{p.company_name}</div>
      <PriorityBadge score={p.priority_score} />
    </div>
    {p.contact_name && <div className="text-white/65 text-[11px] truncate">{p.contact_name}</div>}
    <div className="text-white/45 text-[10px] mt-1">{p.country}{p.city ? ` · ${p.city}` : ''}</div>
    <div className="text-[#00D4FF]/85 text-[10px] mt-1.5 inline-flex items-center gap-1">
      <TargetIcon className="w-3 h-3" /> {p.next_action}
    </div>
    {p.days_since_last_contact !== null && p.days_since_last_contact !== undefined && (
      <div className="text-white/35 text-[10px] mt-1 inline-flex items-center gap-1">
        <Clock className="w-3 h-3" /> {p.days_since_last_contact}d since last touch
      </div>
    )}
  </div>
);

// ─── Bulk Send Email modal (confirmation + composer + live progress) ──
const BulkSendEmailModal = ({ ids, onClose, onDone }) => {
  // phase: 'confirm' (compose) → 'running' (polling) → 'done' | 'failed'
  const [phase, setPhase] = useState('confirm');
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [senderProfileId, setSenderProfileId] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  // Saved Templates (Part 1 & 2)
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [state, setState] = useState({ total: ids.length, completed: 0, sent_count: 0, skipped: 0, failed_count: 0, results: [], mode: 'immediate' });

  // Drip mode controls
  const [mode, setMode] = useState('drip');       // 'immediate' | 'drip'
  const [rateChoice, setRateChoice] = useState('10');  // '10' | '20' | 'custom'
  const [customRate, setCustomRate] = useState(15);    // emails per hour when rate=custom
  const effectiveRate = rateChoice === 'custom' ? Math.max(1, Math.min(500, parseInt(customRate, 10) || 10)) : parseInt(rateChoice, 10);
  const drippingSpacingS = Math.max(1, Math.round(3600 / effectiveRate));
  const estimatedMinutes = mode === 'drip' ? Math.ceil((ids.length * drippingSpacingS) / 60) : Math.max(1, Math.ceil((ids.length / 3) * 2 / 60));

  const loadDefaults = useCallback(async () => {
    setLoadingTpl(true);
    setLoadError(null);
    try {
      // Use the first selected prospect to pre-fill subject + body (server applies
      // per-prospect merge token substitution at SEND time for each row).
      const [tplRes, profRes, savedTplRes] = await Promise.all([
        getProspectEmailTemplate(ids[0]),
        listSenderProfiles(),
        listEmailTemplates('prospect_outreach').catch(() => ({ data: { items: [] } })),
      ]);
      const t = tplRes.data;
      const ps = (profRes.data || []).filter((p) => p.is_active);
      if (!ps.length) throw new Error('No active sender profiles configured.');
      if (!t?.subject || !t?.body_html) throw new Error('Server returned an empty template. The new email API may not be deployed in this environment.');
      setProfiles(ps);
      setSenderProfileId(t.default_sender_profile_id || ps[0].id);
      setSubject(t.subject);
      setBodyHtml(t.body_html);
      setTemplates((savedTplRes.data || {}).items || []);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || 'Unknown error';
      setLoadError(status === 404
        ? `Endpoint not found (404). Redeploy preview → production and try again.\n\nDetail: ${detail}`
        : `${status || 'Network'}: ${detail}`);
    } finally {
      setLoadingTpl(false);
    }
  }, [ids]);

  useEffect(() => { loadDefaults(); }, [loadDefaults]);

  const applyTemplate = (id) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject || '');
    setBodyHtml(t.body_html || '');
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Save current subject + body as a template. Template name:');
    if (!name || !name.trim()) return;
    const language = (window.prompt('Language? Type EN or ES', 'EN') || 'EN').toUpperCase();
    const buyer_tier = (window.prompt('Buyer Tier? A+, A, B, C, or General', 'General') || 'General');
    const market = (window.prompt('Market? Mexico, Central America, South America, Caribbean, Multi-Country, or General', 'General') || 'General');
    try {
      setSavingTemplate(true);
      const r = await createEmailTemplate({
        name: name.trim(),
        subject: subject.trim(),
        body_html: bodyHtml,
        language: ['EN', 'ES'].includes(language) ? language : 'EN',
        buyer_tier: ['A+', 'A', 'B', 'C', 'General'].includes(buyer_tier) ? buyer_tier : 'General',
        market: ['Mexico', 'Central America', 'South America', 'Caribbean', 'Multi-Country', 'General'].includes(market) ? market : 'General',
      });
      const fresh = await listEmailTemplates('prospect_outreach');
      setTemplates(fresh.data?.items || []);
      setSelectedTemplateId(r.data?.id || '');
      toast.success('Template saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const canStart = !loadingTpl && !loadError && subject.trim() && bodyHtml.trim() && senderProfileId;

  // Safely extract a human-readable error message from any axios error.
  // FastAPI 422 returns `detail` as a List[Dict] — the global interceptor
  // already coerces this to a string; `readApiError` is used as a final
  // belt-and-suspenders fallback.
  const _readError = (e, fallback) => readApiError(e, fallback);

  const start = async () => {
    if (!canStart) return;
    const payload = {
      ids,
      sender_profile_id: senderProfileId,
      subject: subject.trim(),
      body_html: bodyHtml,
      mode,
      // 0 for immediate is now valid server-side; safe default for drip.
      rate_per_hour: mode === 'drip' ? effectiveRate : 0,
    };
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[bulk-send] request', { mode, ids_count: ids.length, payload });
    }
    try {
      setPhase('running');
      const { data } = await bulkSendEmail(payload);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[bulk-send] response', data);
      }
      if (!data || !data.job_id) {
        toast.error('Send failed. No response returned.');
        setPhase('failed');
        return;
      }
      setJobId(data.job_id);
      setState((s) => ({ ...s, total: data.total, mode: data.mode || mode, rate_per_hour: data.rate_per_hour, estimated_completion_at: data.estimated_completion_at }));
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[bulk-send] error', e?.response || e);
      }
      toast.error(_readError(e, 'Failed to start bulk send'));
      setPhase('failed');
    }
  };

  useEffect(() => {
    if (phase !== 'running' || !jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await getBulkSendStatus(jobId);
        if (cancelled) return;
        setState(data);
        if (data.status === 'done' || data.status === 'canceled') { setPhase(data.status === 'canceled' ? 'canceled' : 'done'); onDone && onDone(); }
        else if (data.status === 'failed') setPhase('failed');
      } catch (e) {
        if (!cancelled) toast.error(_readError(e, 'Lost connection to send job'));
      }
    };
    tick();
    const iv = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [phase, jobId, onDone]);

  const pct = state.total ? Math.round((state.completed / state.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={phase === 'running' ? undefined : onClose} data-testid="bulk-send-email-modal">
      <div className="bg-[#0A0A0A] border border-[#00D4FF]/40 w-full max-w-[820px] max-h-[90vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#00D4FF]/30 shrink-0">
          <div className="text-[#00D4FF] font-semibold flex items-center gap-2">
            <Send className="w-5 h-5" />
            {phase === 'confirm' ? `Send Email to ${ids.length} Selected Prospect${ids.length === 1 ? '' : 's'}`
              : phase === 'running' ? 'Sending emails…'
              : phase === 'done' ? 'Bulk send complete'
              : phase === 'canceled' ? 'Bulk send canceled' : 'Bulk send failed'}
          </div>
          {phase !== 'running' && (
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="bulk-send-close-btn">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {phase === 'confirm' && (
          loadingTpl ? (
            <div className="p-10 text-center text-white/55 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading template…</div>
          ) : loadError ? (
            <div className="p-6 space-y-4" data-testid="bulk-send-load-error">
              <div className="border border-red-500/40 bg-red-500/10 p-4 text-sm">
                <div className="flex items-start gap-2 text-red-200">
                  <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold mb-1.5">Unable to prepare the email</div>
                    <pre className="text-xs whitespace-pre-wrap font-sans text-red-100/90">{loadError}</pre>
                  </div>
                </div>
              </div>
              <div className="flex justify-end"><button onClick={loadDefaults} className="btn-secondary text-sm">Retry</button></div>
            </div>
          ) : (
            <>
              <div className="overflow-y-auto p-5 space-y-4 text-sm">
                <div className="bg-amber-500/10 border border-amber-500/40 p-3 text-amber-200 text-sm">
                  You are about to send emails to <strong>{ids.length} prospect{ids.length === 1 ? '' : 's'}</strong>.
                  Each recipient gets its own tracking token; merge tokens are substituted per-prospect.
                </div>

                {/* Sending mode */}
                <Field label="Sending mode">
                  <div className="grid grid-cols-2 gap-2" data-testid="bulk-send-mode-picker">
                    <button type="button" onClick={() => setMode('immediate')}
                      disabled={phase === 'running'}
                      className={`px-3 py-2 text-sm border text-left disabled:opacity-30 ${mode === 'immediate' ? 'border-[#00D4FF] bg-[#00D4FF]/15 text-white' : 'border-white/15 text-white/70 hover:border-white/30'}`}
                      data-testid="bulk-send-mode-immediate">
                      <div className="font-semibold flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />Immediate</div>
                      <div className="text-[10px] text-white/55 mt-0.5">3 concurrent SMTP — finishes in seconds. Best for warm contacts.</div>
                    </button>
                    <button type="button" onClick={() => setMode('drip')}
                      disabled={phase === 'running'}
                      className={`px-3 py-2 text-sm border text-left disabled:opacity-30 ${mode === 'drip' ? 'border-[#00D4FF] bg-[#00D4FF]/15 text-white' : 'border-white/15 text-white/70 hover:border-white/30'}`}
                      data-testid="bulk-send-mode-drip">
                      <div className="font-semibold flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Drip · recommended</div>
                      <div className="text-[10px] text-white/55 mt-0.5">Paces sends to mimic a human — kinder to sender reputation, better inbox placement.</div>
                    </button>
                  </div>
                </Field>

                {/* Drip rate selector */}
                {mode === 'drip' && (
                  <Field label="Drip rate">
                    <div className="flex items-center gap-2 flex-wrap" data-testid="bulk-send-drip-rate">
                      {[
                        { val: '10', label: '10 / hour', sub: 'safest' },
                        { val: '20', label: '20 / hour', sub: 'balanced' },
                        { val: 'custom', label: 'Custom', sub: 'set rate' },
                      ].map((opt) => (
                        <button key={opt.val} type="button" onClick={() => setRateChoice(opt.val)}
                          className={`px-3 py-1.5 text-xs border ${rateChoice === opt.val ? 'border-[#00D4FF] bg-[#00D4FF]/15 text-white' : 'border-white/15 text-white/70 hover:border-white/30'}`}
                          data-testid={`bulk-send-rate-${opt.val}`}>
                          {opt.label} <span className="text-[10px] text-white/45 ml-1">{opt.sub}</span>
                        </button>
                      ))}
                      {rateChoice === 'custom' && (
                        <div className="flex items-center gap-1.5">
                          <input type="number" min={1} max={500} value={customRate}
                            onChange={(e) => setCustomRate(e.target.value)}
                            className="w-20 px-2 py-1 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-xs"
                            data-testid="bulk-send-custom-rate-input" />
                          <span className="text-xs text-white/55">/ hour</span>
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-white/45 mt-1.5">
                      ≈ 1 email every {drippingSpacingS}s · estimated completion in{' '}
                      <span className="text-[#00D4FF]" data-testid="bulk-send-eta-label">
                        {estimatedMinutes < 60 ? `${estimatedMinutes} min` : `${Math.round(estimatedMinutes / 60 * 10) / 10} hr`}
                      </span>
                    </div>
                  </Field>
                )}

                <Field label="From (sender profile)">
                  <select value={senderProfileId} onChange={(e) => setSenderProfileId(e.target.value)}
                    className="w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
                    data-testid="bulk-send-sender-select">
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.display_name} &lt;{p.email}&gt;{p.is_default ? ' · default' : ''}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Saved Template (optional)">
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => applyTemplate(e.target.value)}
                      className="flex-1 px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm appearance-none"
                      style={{ colorScheme: 'dark' }}
                      data-testid="bulk-send-template-select"
                    >
                      <option value="">— Choose a template —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.language || '—'} · {t.buyer_tier || '—'} · {t.market || '—'} · {t.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={saveAsTemplate}
                      disabled={savingTemplate || !subject.trim() || !bodyHtml.trim()}
                      className="px-3 py-2 text-xs bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 disabled:opacity-30 whitespace-nowrap"
                      title="Save the current Subject + Body as a reusable template"
                      data-testid="bulk-send-save-template-btn"
                    >
                      {savingTemplate ? 'Saving…' : 'Save as Template'}
                    </button>
                  </div>
                </Field>
                <Field label="Subject (merge tokens applied per-prospect at send time)">
                  <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="bulk-send-subject-input" />
                </Field>
                <Field label="Body (HTML)">
                  <div className="flex items-center justify-end mb-1 gap-2">
                    <button type="button" onClick={() => setShowPreview(!showPreview)}
                      className="text-[10px] uppercase tracking-widest text-[#00D4FF] hover:text-white">
                      {showPreview ? 'Edit HTML' : 'Preview'}
                    </button>
                  </div>
                  {showPreview ? (
                    <div className="border border-white/10 bg-white p-4 text-black text-sm max-h-[280px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                  ) : (
                    <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={11}
                      className="w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-xs font-mono"
                      data-testid="bulk-send-body-input" />
                  )}
                  <div className="text-[10px] text-white/45 mt-1">
                    Merge tokens (substituted per prospect):{' '}
                    <code className="text-[#00D4FF]">{`{first_name}`}</code>{' '}
                    <code className="text-[#00D4FF]">{`{first_name_comma}`}</code>{' '}
                    <code className="text-[#00D4FF]">{`{company}`}</code>{' '}
                    <code className="text-[#00D4FF]">{`{country}`}</code>{' '}
                    <code className="text-[#00D4FF]">{`{city}`}</code>
                  </div>
                </Field>
                <div className="text-[11px] text-white/50 bg-white/[0.02] border border-white/10 p-2.5">
                  <div className="flex items-center gap-1.5 mb-1 text-white/70"><Sparkles className="w-3 h-3" />Per recipient</div>
                  <div>• Status auto-advances to <span className="text-blue-200">Email Sent</span> · last_email_at + next_follow_up_at = +3 days stamped.</div>
                  <div>• Open/click tracking embedded; engagement appears on each prospect's Activity timeline.</div>
                  <div>• 3 concurrent SMTP sends — safer for sender reputation.</div>
                </div>
              </div>
              <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2 shrink-0">
                <button onClick={onClose} disabled={phase === 'running'} className="btn-secondary text-sm disabled:opacity-30" data-testid="bulk-send-cancel-btn">Cancel</button>
                <button onClick={start} disabled={!canStart || phase === 'running'}
                  className="px-4 py-2 text-sm bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30 inline-flex items-center gap-2"
                  data-testid="bulk-send-confirm-btn">
                  {phase === 'running' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Sending…</>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {mode === 'drip'
                        ? `Start drip · ${effectiveRate}/hr · ${ids.length} emails`
                        : `Send to ${ids.length}`}
                    </>
                  )}
                </button>
              </div>
            </>
          )
        )}

        {(phase === 'running' || phase === 'done' || phase === 'failed' || phase === 'canceled') && (
          <div className="overflow-y-auto p-5 space-y-4 text-sm">
            <div>
              <div className="flex justify-between text-xs text-white/65 mb-1">
                <span>Progress</span>
                <span data-testid="bulk-send-progress-label">{state.completed} / {state.total} ({pct}%)</span>
              </div>
              <div className="w-full h-2 bg-white/10 overflow-hidden">
                <div className="h-full bg-[#00D4FF] transition-all duration-300" style={{ width: `${pct}%` }} data-testid="bulk-send-progress-bar" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Counter label="Queued" value={Math.max(0, (state.total || 0) - (state.completed || 0))} color="text-white/85" testId="bulk-send-queued" />
              <Counter label="Sending" value={phase === 'running' ? (state.mode === 'drip' ? (Math.max(0, (state.total || 0) - (state.completed || 0)) > 0 ? 1 : 0) : Math.min(3, Math.max(0, (state.total || 0) - (state.completed || 0)))) : 0} color="text-[#00D4FF]" testId="bulk-send-sending" />
              <Counter label="Sent" value={state.sent_count || 0} color="text-emerald-300" testId="bulk-send-sent" />
              <Counter label="Skipped" value={state.skipped || 0} color="text-amber-300" testId="bulk-send-skipped" />
              <Counter label="Failed" value={state.failed_count || 0} color="text-red-300" testId="bulk-send-failed" />
            </div>
            {state.mode === 'drip' && state.estimated_completion_at && phase === 'running' && (
              <div className="text-[11px] text-white/55 flex items-center gap-1.5" data-testid="bulk-send-eta-running">
                <Clock className="w-3 h-3" />
                Drip at {state.rate_per_hour}/hr · ETA{' '}
                <span className="text-white/85">
                  {(() => {
                    const eta = new Date(state.estimated_completion_at);
                    const mins = Math.max(0, Math.round((eta - new Date()) / 60000));
                    return mins < 60 ? `~${mins} min` : `~${(mins / 60).toFixed(1)} hr`;
                  })()}
                </span>
              </div>
            )}
            {(state.results && state.results.length > 0) && (
              <div className="border border-white/10 max-h-[220px] overflow-y-auto bg-black/40">
                {state.results.slice(-20).reverse().map((r, i) => (
                  <div key={`${r.prospect_id}-${i}`} className="px-3 py-1.5 text-xs border-b border-white/5 flex items-center gap-2">
                    {r.status === 'sent' && <Check className="w-3.5 h-3.5 text-emerald-300 shrink-0" />}
                    {r.status === 'skipped' && <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />}
                    {r.status === 'failed' && <X className="w-3.5 h-3.5 text-red-300 shrink-0" />}
                    <span className="text-white/85 truncate flex-1">{r.company_name || r.prospect_id}</span>
                    <span className="text-white/55 truncate">
                      {r.status === 'sent' && (r.to_email || '')}
                      {r.status === 'skipped' && (r.error || 'no email')}
                      {r.status === 'failed' && (r.error || 'send failed')}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-white/55 flex items-center gap-2">
              {phase === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00D4FF]" />}
              {phase === 'running' && (state.mode === 'drip'
                ? 'Drip job running. Closing this modal does NOT stop it — the queue keeps draining server-side and resumes automatically if the backend restarts.'
                : 'Sending in bursts of 3. Closing this modal does NOT stop the job.')}
              {phase === 'done' && <span className="text-emerald-300">Bulk send complete — table refreshed.</span>}
              {phase === 'canceled' && <span className="text-amber-300">Drip job canceled. Already-sent emails remain sent.</span>}
              {phase === 'failed' && <span className="text-red-300">Job failed.</span>}
            </div>
            {phase === 'running' && state.mode === 'drip' && jobId && (
              <div className="flex justify-end">
                <button onClick={async () => {
                  if (!window.confirm('Cancel this drip job? Already-sent emails stay sent; the remaining queue stops.')) return;
                  try {
                    await cancelBulkSendJob(jobId);
                    toast.success('Drip job canceled');
                  } catch (e) { toast.error('Could not cancel job'); }
                }} className="px-3 py-1.5 text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10"
                   data-testid="bulk-send-cancel-job-btn">
                  Cancel remaining queue
                </button>
              </div>
            )}
            {phase !== 'running' && (
              <div className="flex justify-end">
                <button onClick={onClose} className="btn-primary text-sm" data-testid="bulk-send-done-btn">Close</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Email Analytics card (top of /admin/prospects) ───────────────────
const EmailAnalyticsCard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getProspectEmailAnalytics();
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.status === 404 ? 'analytics endpoint missing (redeploy?)' : 'failed to load');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doReset = async () => {
    try {
      setResetting(true);
      const res = await resetProspectEmailAnalytics({ reset_prospects: true });
      const d = res.data;
      toast.success(
        `Reset: removed ${d.deleted_email_rows} send/open/click rows · ` +
        `${d.reverted_to_not_contacted} prospect(s) reverted to Not Contacted`
      );
      setShowResetConfirm(false);
      await load();
      // Tell parent table to refresh too (a tiny custom event — simplest cross-component nudge)
      window.dispatchEvent(new CustomEvent('prospects:refresh'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  if (err) return (
    <div className="border border-white/10 p-3 text-[11px] text-white/50" data-testid="email-analytics-error">
      Email analytics: {err}
    </div>
  );
  if (!data) return null;

  return (
    <div className="border border-white/10 bg-white/[0.02] p-4" data-testid="email-analytics-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />Outreach Email Analytics
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="text-[10px] text-[#00D4FF] hover:text-white" data-testid="email-analytics-refresh">
            Refresh
          </button>
          <button
            onClick={() => setShowResetConfirm(true)}
            disabled={!data.sent}
            className="text-[10px] text-red-300 hover:text-red-200 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
            title={data.sent ? 'Clear send/open/click history (e.g., after test sends)' : 'Nothing to reset'}
            data-testid="email-analytics-reset">
            <Trash2 className="w-3 h-3" />Reset analytics
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <KpiTile label="Emails Sent" value={data.sent} testId="kpi-sent" />
        <KpiTile label="Open Rate" value={`${data.open_rate}%`} sub={`${data.opened} opens`} testId="kpi-open-rate" color="text-emerald-300" />
        <KpiTile label="Click Rate" value={`${data.click_rate}%`} sub={`${data.clicked} clicks`} testId="kpi-click-rate" color="text-[#00D4FF]" />
        <KpiTile label="Reply Rate" value="—" sub="coming soon" testId="kpi-reply-rate" color="text-white/40" />
        <KpiTile label="Top Country" value={data.top_countries[0]?.country || '—'} sub={data.top_countries[0] ? `${data.top_countries[0].sent} sent` : 'no data'} testId="kpi-top-country" color="text-amber-300" />
      </div>
      {data.top_countries.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/45 mb-1.5">Top Performing Countries</div>
          <div className="space-y-1" data-testid="email-analytics-countries">
            {data.top_countries.slice(0, 5).map((c) => (
              <div key={c.country} className="flex items-center gap-3 text-xs">
                <div className="w-28 text-white/85 truncate">{c.country}</div>
                <div className="flex-1 h-1.5 bg-white/5 relative">
                  <div className="absolute inset-y-0 left-0 bg-emerald-400/60" style={{ width: `${Math.min(c.open_rate, 100)}%` }} />
                </div>
                <div className="text-white/55 w-16 text-right">{c.sent} sent</div>
                <div className="text-emerald-300 w-16 text-right">{c.open_rate}% open</div>
                <div className="text-[#00D4FF] w-16 text-right">{c.click_rate}% click</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showResetConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={resetting ? undefined : () => setShowResetConfirm(false)} data-testid="reset-analytics-modal">
          <div className="bg-[#0A0A0A] border border-red-500/40 w-full max-w-[520px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-red-500/30">
              <div className="text-red-200 font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />Reset email analytics?
              </div>
              {!resetting && <button onClick={() => setShowResetConfirm(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>}
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="text-white/85">
                This clears the <strong>{data.sent}</strong> send {data.sent === 1 ? 'row' : 'rows'} (and their opens/clicks) from the prospect outreach analytics — typically what you want after running test sends.
              </div>
              <div className="text-xs text-white/55 space-y-1">
                <div>• Deletes all email_recipients rows where source=prospect.</div>
                <div>• Reverts affected prospects from <span className="text-blue-200">Email Sent</span> back to <span className="text-white/70">Not Contacted</span>.</div>
                <div>• Removes <code className="text-[#00D4FF]">email_sent</code>/<code className="text-[#00D4FF]">email_opened</code>/<code className="text-[#00D4FF]">email_clicked</code> entries from prospect activity timelines.</div>
                <div className="text-amber-300/85 pt-1">⚠ Already-delivered emails are not recalled. This only resets your dashboard + pipeline.</div>
              </div>
            </div>
            <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setShowResetConfirm(false)} disabled={resetting}
                className="btn-secondary text-sm" data-testid="reset-analytics-cancel">Cancel</button>
              <button onClick={doReset} disabled={resetting}
                className="px-4 py-2 text-sm bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 inline-flex items-center gap-2"
                data-testid="reset-analytics-confirm">
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {resetting ? 'Resetting…' : 'Reset analytics'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KpiTile = ({ label, value, sub, color = 'text-white', testId }) => (
  <div className="bg-white/[0.02] border border-white/10 p-2.5" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-white/45">{label}</div>
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-[10px] text-white/45 mt-0.5">{sub}</div>}
  </div>
);



// ─── Main page ───────────────────────────────────────────────────────
export const AdminProspects = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table'); // 'table' | 'kanban'
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [hasEmailFilter, setHasEmailFilter] = useState('');
  const [hasNameFilter, setHasNameFilter] = useState('');
  const [hasLinkedinFilter, setHasLinkedinFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [countryIssues, setCountryIssues] = useState(false);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [sortBy, setSortBy] = useState('priority_score');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({ countries: [], company_types: [], outreach_statuses: STATUSES_ORDERED });
  const [stats, setStats] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkTier, setBulkTier] = useState('');
  const [bulkTerritory, setBulkTerritory] = useState('');
  const [bulkCountry, setBulkCountry] = useState('');
  const [bulkOwner, setBulkOwner] = useState('');
  const [autoClassifyBusy, setAutoClassifyBusy] = useState(false);
  const [autoMarketsBusy, setAutoMarketsBusy] = useState(false);
  const [bulkEnrich, setBulkEnrich] = useState(null); // { mode } | { ids: [...] } | null
  const [sendEmailFor, setSendEmailFor] = useState(null); // prospect doc | null
  const [bulkSendIds, setBulkSendIds] = useState(null); // array of ids | null

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = { sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (countryFilter) params.country = countryFilter;
      if (typeFilter) params.company_type = typeFilter;
      if (statusFilter) params.outreach_status = statusFilter;
      if (hasEmailFilter) params.has_email = hasEmailFilter === 'yes';
      if (hasNameFilter) params.has_contact_name = hasNameFilter === 'yes';
      if (hasLinkedinFilter) params.has_linkedin = hasLinkedinFilter === 'yes';
      if (tierFilter) params.buyer_potential_tier = tierFilter;
      if (territoryFilter) params.territory = territoryFilter;
      if (languageFilter) params.language = languageFilter;
      if (ownerFilter) params.assigned_owner = ownerFilter;
      if (countryIssues) params.country_issues = true;
      if (sourceFilter.length) params.touched_sources = sourceFilter;
      const r = await listProspects(params);
      setRows(r.data || []);
    } catch (e) { toast.error('Failed to load prospects'); }
    finally { setLoading(false); }
  }, [search, countryFilter, typeFilter, statusFilter, hasEmailFilter, hasNameFilter, hasLinkedinFilter, tierFilter, territoryFilter, languageFilter, ownerFilter, countryIssues, sourceFilter, sortBy, sortDir]);

  const refreshMeta = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([getProspectFilters(), getProspectStats()]);
      setFilters({ ...f.data, outreach_statuses: STATUSES_ORDERED });
      setStats(s.data);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Hot Leads Inbox deep-link: /admin/prospects?compose=<prospect_id>
  // Auto-opens the Send Email modal for that prospect after fetch.
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase 5.3 — remember when the composer was opened from the Hot
  // Leads inbox so the close handler can navigate back instead of
  // stranding the user on /admin/prospects.
  const [composeOpenedFrom, setComposeOpenedFrom] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const composeId = searchParams.get('compose');
    if (!composeId || sendEmailFor) return;
    (async () => {
      try {
        const r = await getProspectById(composeId);
        setSendEmailFor(r.data);
        // Phase 5.3 — the Hot Leads Inbox is rendered inside the
        // admin dashboard at /admin, not on a dedicated route. Send
        // the user back there on close so they see the inbox again.
        setComposeOpenedFrom('/admin');
        // Clean up the query string so reloads don't re-trigger.
        const next = new URLSearchParams(searchParams);
        next.delete('compose');
        setSearchParams(next, { replace: true });
      } catch (e) {
        toast.error('Could not open composer — prospect not found');
      }
    })();
  }, [searchParams, sendEmailFor, setSearchParams]);

  // Refresh trigger from child components (e.g., EmailAnalyticsCard after reset)
  useEffect(() => {
    const handler = () => { fetchRows(); refreshMeta(); };
    window.addEventListener('prospects:refresh', handler);
    return () => window.removeEventListener('prospects:refresh', handler);
  }, [fetchRows, refreshMeta]);
  useEffect(() => { refreshMeta(); }, [refreshMeta, rows.length]);

  const handleSort = (field, dir) => { setSortBy(field); setSortDir(dir); };

  const handleStatusChange = async (p, newStatus) => {
    try {
      const r = await updateProspect(p.id, { outreach_status: newStatus });
      setRows((prev) => prev.map((row) => row.id === p.id ? r.data : row));
    } catch (e) { toast.error('Status update failed'); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Remove "${p.company_name}"?`)) return;
    try { await deleteProspect(p.id); toast.success('Removed'); fetchRows(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const handleSendEmail = (p) => {
    // Opens the in-app Send Email modal instead of mailto:
    const hasEmail = !!(p.contact_email || p.public_email);
    if (!hasEmail) return toast.error('No email on this prospect — enrich or add one first');
    setSendEmailFor(p);
  };
  const handleSendWa = async (p) => {
    const url = buildWaUrl(p);
    if (!url) return toast.error('No WhatsApp/phone on this prospect');
    window.open(url, '_blank');
    try {
      setBusyId(p.id);
      const r = await markWaSent(p.id);
      setRows((prev) => prev.map((row) => row.id === p.id ? r.data : row));
      toast.success(`WhatsApp tracked → ${r.data.outreach_status}`);
    } catch (e) { toast.error('Could not stamp WhatsApp'); }
    finally { setBusyId(null); refreshMeta(); }
  };
  const handleCreateQuote = async (p) => {
    window.open('/admin/quotes', '_blank');
    try {
      setBusyId(p.id);
      const r = await markQuoteSent(p.id);
      setRows((prev) => prev.map((row) => row.id === p.id ? r.data : row));
      toast.success(`Quote Sent · ${p.company_name}`);
    } catch (e) { toast.error('Could not mark quote sent'); }
    finally { setBusyId(null); refreshMeta(); }
  };
  const handleMarkCustomer = async (p) => {
    if (!window.confirm(`Mark "${p.company_name}" as Customer? This creates a subscriber if not already linked.`)) return;
    try {
      setBusyId(p.id);
      const r = await markCustomer(p.id);
      setRows((prev) => prev.map((row) => row.id === p.id ? r.data.prospect : row));
      toast.success(r.data.message || 'Customer won 🎉');
    } catch (e) { toast.error(e.response?.data?.detail || 'Mark customer failed'); }
    finally { setBusyId(null); refreshMeta(); }
  };

  // Phase 3 — Convert Prospect → real Buyer (users collection)
  const [convertBuyerModal, setConvertBuyerModal] = useState(null); // { prospect, sendEmail }

  const runConvertToBuyer = async (p, sendEmail, allowDuplicate = false) => {
    try {
      setBusyId(p.id);
      const r = await convertProspectToBuyer(p.id, allowDuplicate, sendEmail);
      toast.success(r.data.message || 'Buyer account created');
      setConvertBuyerModal(null);
      fetchRows();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === 'duplicate_buyer') {
        if (window.confirm(`${detail.message}\n\nLink this prospect to the existing buyer account instead?`)) {
          // Duplicate override path does NOT send email (per requirement #5)
          await runConvertToBuyer(p, false, true);
          return;
        }
        setConvertBuyerModal(null);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Convert to buyer failed'));
      }
    } finally {
      setBusyId(null);
      refreshMeta();
    }
  };

  const handleConvertToBuyer = (p) => {
    if (p.buyer_user_id) { toast.info('Already a buyer'); return; }
    setConvertBuyerModal({ prospect: p, sendEmail: true });
  };
  const handleEnrich = async (p) => {
    try {
      setBusyId(p.id);
      const r = await enrichProspect(p.id);
      const filled = r.data.filled_fields || [];
      filled.length === 0 ? toast.message('No new data', { description: r.data.message }) : toast.success(`Filled: ${filled.join(', ')}`);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Enrich failed'); }
    finally { setBusyId(null); }
  };

  // Selection + bulk
  const allSelectedOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((s) => {
      if (allSelectedOnPage) return new Set();
      const n = new Set(s); rows.forEach((r) => n.add(r.id)); return n;
    });
  };
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const runBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !window.confirm(`Delete ${ids.length} selected?`)) return;
    try { setBulkBusy(true); const r = await bulkProspectAction({ ids, action: 'delete' });
      toast.success(`Deleted ${r.data.deleted}`); setSelected(new Set()); fetchRows();
    } catch (e) { toast.error('Bulk delete failed'); } finally { setBulkBusy(false); }
  };
  const runBulkStatus = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !bulkStatus) return;
    try { setBulkBusy(true);
      const r = await bulkProspectAction({ ids, action: 'update_status', outreach_status: bulkStatus });
      toast.success(`${r.data.updated} → ${r.data.new_status}`);
      setSelected(new Set()); setBulkStatus(''); fetchRows();
    } catch (e) { toast.error('Bulk update failed'); } finally { setBulkBusy(false); }
  };
  const runBulkTier = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !BUYER_TIERS.includes(bulkTier)) return;
    try { setBulkBusy(true);
      const r = await bulkProspectAction({ ids, action: 'set_buyer_tier', buyer_potential_tier: bulkTier });
      const { updated = 0, action_autofilled = 0 } = r.data || {};
      toast.success(`${updated} → tier ${bulkTier} · ${action_autofilled} action${action_autofilled === 1 ? '' : 's'} auto-filled`);
      setSelected(new Set()); setBulkTier(''); fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk tier update failed'); } finally { setBulkBusy(false); }
  };

  const runBulkTerritory = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !TERRITORIES.includes(bulkTerritory)) return;
    try { setBulkBusy(true);
      const r = await bulkProspectAction({ ids, action: 'set_territory', territory: bulkTerritory });
      toast.success(`${r.data.updated} → ${r.data.new_territory}`);
      setSelected(new Set()); setBulkTerritory(''); fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk market update failed'); } finally { setBulkBusy(false); }
  };

  const runBulkCountry = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !ALLOWED_COUNTRIES.includes(bulkCountry)) return;
    try { setBulkBusy(true);
      const r = await bulkProspectAction({ ids, action: 'set_country', country: bulkCountry });
      toast.success(`${r.data.updated} → ${r.data.new_country}`);
      setSelected(new Set()); setBulkCountry(''); fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk country update failed'); } finally { setBulkBusy(false); }
  };

  const runBulkOwner = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !bulkOwner) return;
    try { setBulkBusy(true);
      const r = await bulkProspectAction({ ids, action: 'assign_owner', assigned_owner: bulkOwner });
      toast.success(`${r.data.updated} → ${r.data.new_owner}`);
      setSelected(new Set()); setBulkOwner(''); fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk owner update failed'); } finally { setBulkBusy(false); }
  };

  const runAutoClassify = async () => {
    try {
      setAutoClassifyBusy(true);
      const dry = await autoClassifyProspectTiers({ dryRun: true });
      const { candidates = 0, would_update = 0, breakdown = {} } = dry.data || {};
      if (!candidates) {
        toast.info('No prospects qualify (need tier=B and empty annual units).');
        return;
      }
      const summary =
        `Scan complete:\n\n` +
        `• ${candidates} candidate prospect${candidates === 1 ? '' : 's'}\n` +
        `• Will reclassify: ${would_update}\n\n` +
        `Breakdown:\n` +
        `  A+ ${breakdown['A+'] || 0}\n` +
        `  A  ${breakdown['A'] || 0}\n` +
        `  B  ${breakdown['B'] || 0} (no change)\n` +
        `  C  ${breakdown['C'] || 0}\n\n` +
        `Apply changes now?`;
      // eslint-disable-next-line no-alert
      if (!window.confirm(summary)) return;
      const applied = await autoClassifyProspectTiers({ dryRun: false });
      const { updated = 0, action_autofilled = 0 } = applied.data || {};
      toast.success(`Auto-classified ${updated} prospect${updated === 1 ? '' : 's'} · ${action_autofilled} action${action_autofilled === 1 ? '' : 's'} auto-filled`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-classify failed');
    } finally {
      setAutoClassifyBusy(false);
    }
  };

  const runAutoAssignMarkets = async () => {
    try {
      setAutoMarketsBusy(true);
      // First pass: fill-blank-only dry run
      const dry = await autoAssignProspectMarkets({ dryRun: true, overwrite: false });
      const { candidates = 0, would_update = 0, breakdown = {} } = dry.data || {};
      // Also look up overwrite stats so we can offer it if blanks are zero.
      let overwriteOffered = false;
      let overwriteDry = null;
      if (candidates === 0) {
        overwriteDry = (await autoAssignProspectMarkets({ dryRun: true, overwrite: true })).data || {};
        overwriteOffered = (overwriteDry.would_update || 0) > 0;
      }
      if (!candidates && !overwriteOffered) {
        toast.info('All prospects already have a Market assigned.');
        return;
      }
      const lines = [
        'Auto Assign Markets Preview',
        '',
        `Candidates: ${candidates}`,
        `Will update: ${would_update}`,
        '',
        `Mexico:          ${breakdown['Mexico'] || 0}`,
        `Central America: ${breakdown['Central America'] || 0}`,
        `South America:   ${breakdown['South America'] || 0}`,
        `Caribbean:       ${breakdown['Caribbean'] || 0}`,
        `Multi-Country:   ${breakdown['Multi-Country'] || 0}`,
        '',
        candidates === 0 && overwriteOffered
          ? `No blank Markets. Re-assign ALL ${overwriteDry.candidates} prospects from Country? (overwrite existing)`
          : 'Apply changes now?',
      ];
      // eslint-disable-next-line no-alert
      const ok = window.confirm(lines.join('\n'));
      if (!ok) return;
      const overwrite = candidates === 0 && overwriteOffered;
      const applied = await autoAssignProspectMarkets({ dryRun: false, overwrite });
      const { updated = 0 } = applied.data || {};
      toast.success(`Auto-assigned Market on ${updated} prospect${updated === 1 ? '' : 's'}`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-assign markets failed');
    } finally {
      setAutoMarketsBusy(false);
    }
  };

  // Export
  const downloadBlob = async (qs) => {
    try {
      const r = await exportProspects(qs);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `luxortek-prospects-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url); toast.success('Export downloaded');
    } catch (e) { toast.error('Export failed'); }
  };
  const exportFiltered = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (countryFilter) params.set('country', countryFilter);
    if (typeFilter) params.set('company_type', typeFilter);
    if (statusFilter) params.set('outreach_status', statusFilter);
    if (hasEmailFilter) params.set('has_email', hasEmailFilter === 'yes');
    if (hasNameFilter) params.set('has_contact_name', hasNameFilter === 'yes');
    if (hasLinkedinFilter) params.set('has_linkedin', hasLinkedinFilter === 'yes');
    if (tierFilter) params.set('buyer_potential_tier', tierFilter);
    if (territoryFilter) params.set('territory', territoryFilter);
    if (ownerFilter) params.set('owner', ownerFilter);
    if (countryIssues) params.set('country_issues', 'true');
    sourceFilter.forEach((s) => params.append('touched_sources', s));
    downloadBlob(params.toString());
  };
  const exportSelected = () => {
    const ids = Array.from(selected);
    if (!ids.length) return toast.error('Select prospects first');
    downloadBlob(`ids=${ids.join(',')}`);
  };

  // Kanban drag-drop
  const onDragStartCard = (e, id) => { e.dataTransfer.setData('prospect_id', id); };
  const onDropColumn = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('prospect_id');
    if (!id) return;
    const p = rows.find((r) => r.id === id);
    if (!p || p.outreach_status === status) return;
    await handleStatusChange(p, status);
    refreshMeta();
  };

  const clearFilters = () => {
    setSearch(''); setCountryFilter(''); setTypeFilter(''); setStatusFilter('');
    setHasEmailFilter(''); setHasNameFilter(''); setHasLinkedinFilter(''); setTierFilter(''); setTerritoryFilter('');
    setOwnerFilter(''); setCountryIssues(false); setSourceFilter([]);
  };
  const filtersActive = useMemo(() => !!(search || countryFilter || typeFilter || statusFilter || hasEmailFilter || hasNameFilter || hasLinkedinFilter || tierFilter || territoryFilter || ownerFilter || countryIssues || sourceFilter.length),
    [search, countryFilter, typeFilter, statusFilter, hasEmailFilter, hasNameFilter, hasLinkedinFilter, tierFilter, territoryFilter, ownerFilter, countryIssues, sourceFilter]);

  // Dashboard cards
  const MetricCard = ({ icon: Icon, label, value, accent, suffix }) => (
    <div className="border border-white/10 bg-[#0A0A0A] p-3.5">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-white/45">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-white/30" />}
      </div>
      <div className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}{suffix || ''}</div>
    </div>
  );

  // Group by status for Kanban
  const grouped = useMemo(() => {
    const out = Object.fromEntries(STATUSES_ORDERED.map((s) => [s, []]));
    rows.forEach((r) => {
      const s = STATUSES_ORDERED.includes(r.outreach_status) ? r.outreach_status : 'Not Contacted';
      out[s].push(r);
    });
    return out;
  }, [rows]);

  return (
    <div className="p-6" data-testid="admin-prospects-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Prospects</h1>
          <p className="text-white/55 text-sm">B2B sales pipeline · LATAM distributor outreach</p>
        </div>
        <div className="flex gap-2 self-start flex-wrap">
          <div className="inline-flex border border-white/10 overflow-hidden" data-testid="view-switcher">
            <button onClick={() => setView('table')} className={`px-3 py-2 text-xs ${view==='table' ? 'bg-[#00D4FF] text-black' : 'text-white/70 hover:text-white'}`} data-testid="view-table-btn">
              <TableIcon className="w-3.5 h-3.5 inline mr-1" />Table
            </button>
            <button onClick={() => setView('kanban')} className={`px-3 py-2 text-xs ${view==='kanban' ? 'bg-[#00D4FF] text-black' : 'text-white/70 hover:text-white'}`} data-testid="view-kanban-btn">
              <LayoutGrid className="w-3.5 h-3.5 inline mr-1" />Kanban
            </button>
          </div>
          <button onClick={() => setShowImport(true)} className="btn-secondary inline-flex items-center gap-2" data-testid="import-prospects-btn"><Upload className="w-4 h-4" />Import CSV</button>
          <button onClick={exportFiltered} className="btn-secondary inline-flex items-center gap-2" data-testid="export-filtered-btn"><Download className="w-4 h-4" />Export</button>
          <button onClick={() => setBulkEnrich({ mode: 'not_contacted_missing_info' })}
                  className="px-3 py-2 text-sm bg-violet-500/10 border border-violet-500/40 text-violet-200 hover:bg-violet-500/25 hover:text-white inline-flex items-center gap-2"
                  title='Scan websites of all "Not Contacted" prospects with missing email / phone / WhatsApp / LinkedIn'
                  data-testid="enrich-all-not-contacted-btn">
            <Sparkles className="w-4 h-4" />Enrich Missing Info
          </button>
          <button onClick={runAutoClassify}
                  disabled={autoClassifyBusy}
                  className="px-3 py-2 text-sm bg-amber-500/10 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25 hover:text-white inline-flex items-center gap-2 disabled:opacity-40"
                  title="One-time: scan company types and auto-assign A+/A/B/C tier (only for prospects at tier B with empty annual units)."
                  data-testid="auto-classify-tiers-btn">
            <Sparkles className="w-4 h-4" />{autoClassifyBusy ? 'Classifying…' : 'Auto Assign Tiers'}
          </button>
          <button onClick={runAutoAssignMarkets}
                  disabled={autoMarketsBusy}
                  className="px-3 py-2 text-sm bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/25 hover:text-white inline-flex items-center gap-2 disabled:opacity-40"
                  title="Populate Market from Country. Default: fills only blank markets. Re-run if all markets are set to offer overwrite mode."
                  data-testid="auto-assign-markets-btn">
            <Sparkles className="w-4 h-4" />{autoMarketsBusy ? 'Assigning…' : 'Auto Assign Markets'}
          </button>
          <button onClick={() => setEditing({})} className="btn-primary inline-flex items-center gap-2" data-testid="new-prospect-btn"><Plus className="w-4 h-4" />Add Prospect</button>
          <button onClick={() => setShowDeleteAll(true)} className="px-3 py-2 text-sm bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 inline-flex items-center gap-2" data-testid="delete-all-btn"><Trash2 className="w-4 h-4" />Delete All</button>
        </div>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="prospect-metrics">
        <MetricCard icon={ListChecks} label="Total Prospects" value={stats.total || 0} />
        <MetricCard icon={Send} label="Contacted" value={stats.contacted || 0} accent="text-blue-300" />
        <MetricCard icon={MessageSquare} label="Interested" value={stats.interested || 0} accent="text-emerald-300" />
        <MetricCard icon={FileCheck} label="Quotes Sent" value={stats.quotes_sent || 0} accent="text-teal-300" />
        <MetricCard icon={Crown} label="Customers Won" value={stats.customers || 0} accent="text-amber-300" />
        <MetricCard icon={TrendingUp} label="Conversion Rate" value={stats.conversion_rate || 0} accent="text-[#00D4FF]" suffix="%" />
      </div>

      {/* Email outreach analytics */}
      <div className="mb-5">
        <EmailAnalyticsCard />
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4 text-xs">
        <div className="col-span-2 lg:col-span-2 relative">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="prospects-search" />
        </div>
        <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-country">
          <option value="">All countries</option>{(filters.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-type">
          <option value="">All types</option>{(filters.company_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-status">
          <option value="">All statuses</option>{STATUSES_ORDERED.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={hasEmailFilter} onChange={(e) => setHasEmailFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-has-email">
          <option value="">Any email</option><option value="yes">Has email</option><option value="no">No email</option>
        </select>
        <select value={hasNameFilter} onChange={(e) => setHasNameFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-has-name">
          <option value="">Any name</option><option value="yes">Has contact name</option><option value="no">No contact name</option>
        </select>
        <div className="col-span-2 md:col-span-1 flex gap-2">
          <select value={hasLinkedinFilter} onChange={(e) => setHasLinkedinFilter(e.target.value)} className="flex-1 px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-has-linkedin">
            <option value="">Any LinkedIn</option><option value="yes">Has LinkedIn</option><option value="no">No LinkedIn</option>
          </select>
          {filtersActive && <button onClick={clearFilters} className="px-2 py-2 border border-white/10 text-white/55 hover:text-white" title="Clear"><X className="w-3 h-3" /></button>}
        </div>
        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-buyer-tier">
          <option value="">All buyer tiers</option>
          {BUYER_TIERS.map((t) => <option key={t} value={t}>{t} — {TIER_LABELS[t]}</option>)}
        </select>
        <select value={territoryFilter} onChange={(e) => setTerritoryFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-territory">
          <option value="">All markets</option>
          {TERRITORIES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-language">
          <option value="">All languages</option>
          {['English', 'Spanish', 'Portuguese', 'Unknown'].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="px-2 py-2 bg-[#050505] border border-white/10 text-white focus:border-[#00D4FF] outline-none" data-testid="filter-owner">
          <option value="">All Owners</option>
          {OWNERS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setCountryIssues((v) => !v)}
          className={`px-2 py-2 text-xs border outline-none ${countryIssues ? 'border-amber-500/60 bg-amber-500/15 text-amber-200' : 'border-white/10 bg-[#050505] text-white/65 hover:bg-white/5'}`}
          title="Show only prospects with Unknown / Multi-Country / empty country"
          data-testid="filter-country-issues"
        >
          {countryIssues ? '⚠ Country Issues ON' : 'Country Issues'}
        </button>
      </div>

      {/* Touched Sources filter — how this prospect entered / interacted with Luxor Tek */}
      <div className="flex items-center flex-wrap gap-2 mb-4 text-xs" data-testid="touched-sources-filter">
        <span className="text-white/40 mr-1">Touched sources:</span>
        {[
          { key: 'buyer_application',   label: 'Buyer Application' },
          { key: 'sourcing_request',    label: 'Sourcing Request' },
          { key: 'quote_request',       label: 'Quote Request' },
          { key: 'contact_form',        label: 'Contact Form' },
          { key: 'order',               label: 'Order' },
          { key: 'rfq_campaign_reply',  label: 'RFQ Reply' },
        ].map(({ key, label }) => {
          const active = sourceFilter.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSourceFilter((cur) => cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key])}
              className={`px-2.5 py-1 border transition-colors ${
                active
                  ? 'bg-[#00D4FF]/15 border-[#00D4FF]/50 text-[#00D4FF]'
                  : 'border-white/10 text-white/50 hover:text-white/80 hover:border-white/25'
              }`}
              data-testid={`touched-source-${key}`}
              aria-pressed={active}
            >
              {label}
            </button>
          );
        })}
        {sourceFilter.length > 0 && (
          <button
            type="button"
            onClick={() => setSourceFilter([])}
            className="text-[11px] text-white/30 hover:text-white/60 ml-1"
            data-testid="clear-touched-sources"
          >
            Clear
          </button>
        )}
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="border border-[#00D4FF]/40 bg-[#00D4FF]/5 p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="bulk-action-bar">
          <ListChecks className="w-4 h-4 text-[#00D4FF]" />
          <span className="text-[#00D4FF] text-sm">{selected.size} selected</span>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="px-2 py-1 bg-[#050505] border border-white/10 text-white text-xs focus:border-[#00D4FF] outline-none">
            <option value="">— update status to —</option>{STATUSES_ORDERED.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={runBulkStatus} disabled={!bulkStatus || bulkBusy} className="px-3 py-1 text-xs bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30">Apply</button>
          <select value={bulkTier} onChange={(e) => setBulkTier(e.target.value)} className="px-2 py-1 bg-[#050505] border border-white/10 text-white text-xs focus:border-[#00D4FF] outline-none" data-testid="bulk-tier-select">
            <option value="">— set buyer tier to —</option>{BUYER_TIERS.map((t) => <option key={t} value={t}>{t} — {TIER_LABELS[t]}</option>)}
          </select>
          <button onClick={runBulkTier} disabled={!bulkTier || bulkBusy} className="px-3 py-1 text-xs bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30" data-testid="bulk-tier-apply">Apply</button>
          <select value={bulkTerritory} onChange={(e) => setBulkTerritory(e.target.value)} className="px-2 py-1 bg-[#050505] border border-white/10 text-white text-xs focus:border-[#00D4FF] outline-none" data-testid="bulk-territory-select">
            <option value="">— set market to —</option>{TERRITORIES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={runBulkTerritory} disabled={!bulkTerritory || bulkBusy} className="px-3 py-1 text-xs bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30" data-testid="bulk-territory-apply">Apply</button>
          <select value={bulkCountry} onChange={(e) => setBulkCountry(e.target.value)} className="px-2 py-1 bg-[#050505] border border-white/10 text-white text-xs focus:border-[#00D4FF] outline-none" data-testid="bulk-country-select">
            <option value="">— set country to —</option>{ALLOWED_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={runBulkCountry} disabled={!bulkCountry || bulkBusy} className="px-3 py-1 text-xs bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30" data-testid="bulk-country-apply">Apply</button>
          <select value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)} className="px-2 py-1 bg-[#050505] border border-white/10 text-white text-xs focus:border-[#00D4FF] outline-none" data-testid="bulk-owner-select">
            <option value="">— assign owner —</option>{OWNERS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={runBulkOwner} disabled={!bulkOwner || bulkBusy} className="px-3 py-1 text-xs bg-[#00D4FF] text-black hover:bg-[#00B8E6] disabled:opacity-30" data-testid="bulk-owner-apply">Apply</button>
          <button onClick={() => setBulkEnrich({ ids: Array.from(selected) })}
                  disabled={bulkBusy}
                  className="px-3 py-1 text-xs bg-violet-500/15 border border-violet-500/40 text-violet-200 hover:bg-violet-500/30 hover:text-white inline-flex items-center gap-1"
                  data-testid="bulk-enrich-selected-btn"
                  title="Enrich selected prospects from their websites">
            <Sparkles className="w-3 h-3" />Bulk Enrich Selected
          </button>
          <button onClick={() => setBulkSendIds(Array.from(selected))}
                  disabled={bulkBusy}
                  className="px-3 py-1 text-xs bg-[#00D4FF]/15 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30 hover:text-white inline-flex items-center gap-1"
                  data-testid="bulk-send-email-selected-btn"
                  title="Send the same email (with per-prospect merge tokens) to all selected prospects">
            <Send className="w-3 h-3" />Send Email to Selected
          </button>
          <button onClick={exportSelected} className="px-3 py-1 text-xs border border-white/10 text-white/80 hover:border-white/30 inline-flex items-center gap-1"><Download className="w-3 h-3" />Export selected</button>
          <button onClick={runBulkDelete} disabled={bulkBusy} className="px-3 py-1 text-xs bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete selected</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-white/55 hover:text-white text-xs">Clear selection</button>
        </div>
      )}

      {/* TABLE VIEW */}
      {view === 'table' && (
        loading ? <p className="text-white/50 text-sm">Loading…</p>
        : rows.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center">
            <p className="text-white/55 text-sm mb-2">No prospects match the current filters.</p>
            <p className="text-white/40 text-xs">Click <span className="text-[#00D4FF]">Import CSV</span> or <span className="text-[#00D4FF]">Add Prospect</span>.</p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm table-fixed" data-testid="prospects-table">
              <colgroup>
                <col style={{ width: '34px' }} />
                <col style={{ width: '60px' }} />
                <col style={{ width: '70px' }} />
                <col style={{ width: '260px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '220px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '160px' }} />
                <col style={{ width: '90px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '300px' }} />
              </colgroup>
              <thead className="bg-[#0A0A0A] text-white/55 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-3"><input type="checkbox" checked={allSelectedOnPage} onChange={toggleAll} data-testid="select-all-checkbox" /></th>
                  <SortHeader label="Priority" field="priority_score" sortBy={sortBy} sortDir={sortDir} onChange={handleSort} />
                  <SortHeader label="Buyer" field="buyer_potential_tier" sortBy={sortBy} sortDir={sortDir} onChange={handleSort} />
                  <SortHeader label="Company / Contact" field="company_name" sortBy={sortBy} sortDir={sortDir} onChange={handleSort} />
                  <SortHeader label="Country" field="country" sortBy={sortBy} sortDir={sortDir} onChange={handleSort} />
                  <th className="text-left px-3 py-3">Market</th>
                  <th className="text-left px-3 py-3">Email</th>
                  <th className="text-left px-3 py-3">WhatsApp</th>
                  <th className="text-left px-3 py-3">Next Action</th>
                  <SortHeader label="Status" field="outreach_status" sortBy={sortBy} sortDir={sortDir} onChange={handleSort} />
                  <th className="text-left px-3 py-3">Owner</th>
                  <SortHeader label="Last Activity" field="last_activity" sortBy={sortBy} sortDir={sortDir} onChange={handleSort} />
                  <th className="px-3 py-3 sticky right-0 z-20 bg-[#0A0A0A] text-right border-l border-white/10 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const hasEmail = !!(p.contact_email || p.public_email);
                  const hasWa = !!(p.whatsapp || p.public_phone);
                  const emailToShow = p.contact_email || p.public_email || '';
                  const phoneRaw = p.whatsapp || p.public_phone || '';
                  const phoneDisplay = formatPhoneDisplay(phoneRaw);
                  return (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02] group" data-testid={`prospect-row-${p.id}`}>
                    <td className="px-3 py-3 align-top"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} /></td>
                    <td className="px-2 py-3 align-top"><PriorityBadge score={p.priority_score} /></td>
                    <td className="px-2 py-3 align-top">
                      <TierBadge tier={p.buyer_potential_tier} />
                      {p.estimated_annual_units != null && (
                        <div className="text-white/35 text-[10px] mt-0.5">{p.estimated_annual_units}u/yr</div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top overflow-hidden">
                      <div className="text-white font-medium truncate" title={p.company_name} data-testid={`prospect-company-${p.id}`}>{p.company_name}</div>
                      {p.contact_name ? (
                        <div className="text-white/65 text-xs mt-0.5 truncate" title={p.contact_name}><span className="text-white/85">{p.contact_name}</span>{p.contact_title ? ` · ${p.contact_title}` : ''}</div>
                      ) : (
                        <div className="text-white/30 text-[11px] mt-0.5 italic">no contact name</div>
                      )}
                      {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-[#00D4FF] text-[11px] hover:underline inline-flex items-center gap-1 mt-0.5 truncate max-w-full"><Globe className="w-3 h-3 shrink-0" /><span className="truncate">{p.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]}</span></a>}
                    </td>
                    <td className="px-3 py-3 text-white/75 align-top">
                      <div className="text-sm truncate" title={p.city ? `${p.country} · ${p.city}` : p.country}>{p.country}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      {p.territory ? (
                        <span className="inline-block text-[10px] px-1.5 py-0.5 bg-white/[0.06] border border-white/15 text-white/75 tracking-wide" data-testid={`territory-${p.id}`}>
                          {p.territory}
                        </span>
                      ) : <span className="text-white/25 text-[11px]">—</span>}
                    </td>
                    <td className="px-3 py-3 align-top text-xs overflow-hidden">
                      {hasEmail ? (
                        <a href={`mailto:${emailToShow}`} className="text-[#00D4FF] hover:underline block truncate" title={emailToShow}>{emailToShow}</a>
                      ) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-3 py-3 align-top text-xs overflow-hidden">
                      {hasWa ? (
                        <span className="text-green-300 block truncate" title={phoneRaw}>{phoneDisplay}</span>
                      ) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-3 py-3 align-top text-xs overflow-hidden">
                      <div className="text-[#00D4FF]/90 truncate" title={p.next_action}>{p.next_action}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <select value={p.outreach_status} onChange={(e) => handleStatusChange(p, e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white text-xs px-2 py-1 focus:border-[#00D4FF] outline-none mb-1" data-testid={`prospect-status-${p.id}`}>
                        {STATUSES_ORDERED.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div><StatusBadge status={p.outreach_status} /></div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className="text-[11px] text-white/65" data-testid={`owner-${p.id}`}>
                        {p.assigned_owner || DEFAULT_OWNER}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <LastActivityCell value={p.last_activity} />
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap align-top sticky right-0 z-10 bg-[#050505] group-hover:bg-[#0a0a0a] border-l border-white/10 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.8)]">
                      <div className="flex gap-1 justify-end items-center">
                        <ActionIconBtn
                          onClick={() => handleSendEmail(p)}
                          disabled={!hasEmail || busyId === p.id}
                          tooltip={hasEmail ? 'Send email (in-app, tracked)' : 'No email on file — enrich or add one'}
                          color="blue"
                          icon={Mail}
                          testId={`send-email-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => handleSendWa(p)}
                          disabled={!hasWa || busyId === p.id}
                          tooltip={hasWa ? 'Send WhatsApp message' : 'No WhatsApp number on file'}
                          color="green"
                          icon={MessageCircle}
                          testId={`send-wa-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => handleCreateQuote(p)}
                          disabled={busyId === p.id}
                          tooltip="Mark quote sent"
                          color="teal"
                          icon={FileCheck}
                          testId={`create-quote-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => handleMarkCustomer(p)}
                          disabled={busyId === p.id || p.outreach_status === 'Customer'}
                          tooltip={p.outreach_status === 'Customer' ? 'Already a customer' : 'Convert to customer'}
                          color="amber"
                          icon={Crown}
                          testId={`mark-customer-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => handleConvertToBuyer(p)}
                          disabled={busyId === p.id || !!p.buyer_user_id}
                          tooltip={p.buyer_user_id ? 'Already a buyer' : 'Convert to Buyer account'}
                          color="emerald"
                          icon={UserPlus}
                          testId={`convert-buyer-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => handleEnrich(p)}
                          disabled={busyId === p.id || !p.website}
                          tooltip={p.website ? 'Enrich from website (find email/phone/WhatsApp)' : 'Add a website first to enrich'}
                          color="violet"
                          icon={Sparkles}
                          testId={`enrich-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => setEditing(p)}
                          tooltip="Edit prospect & view activity"
                          color="cyan"
                          icon={Pencil}
                          testId={`edit-prospect-btn-${p.id}`}
                        />
                        <ActionIconBtn
                          onClick={() => handleDelete(p)}
                          tooltip="Delete prospect"
                          color="red"
                          icon={Trash2}
                          testId={`delete-prospect-btn-${p.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
            <div className="px-3 py-2 text-[11px] text-white/40 bg-[#0A0A0A] border-t border-white/5">
              {rows.length} shown · sorted by {sortBy.replace(/_/g, ' ')} {sortDir}
            </div>
          </div>
        )
      )}

      {/* KANBAN VIEW */}
      {view === 'kanban' && (
        <div className="overflow-x-auto" data-testid="kanban-board">
          <div className="flex gap-3 min-w-max pb-3">
            {STATUSES_ORDERED.map((status) => (
              <div
                key={status}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropColumn(e, status)}
                className="w-[260px] flex-shrink-0 border border-white/10 bg-[#0A0A0A] flex flex-col max-h-[70vh]"
                data-testid={`kanban-col-${status.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className={`px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center justify-between ${STATUS_COLORS[status]}`}>
                  <span>{status}</span>
                  <span>{grouped[status].length}</span>
                </div>
                <div className="p-2 overflow-y-auto flex-1">
                  {grouped[status].length === 0 ? (
                    <div className="text-white/25 text-[11px] p-2 italic">Drag cards here</div>
                  ) : grouped[status].map((p) => (
                    <KanbanCard key={p.id} p={p} onDragStart={onDragStartCard} onEdit={(x) => setEditing(x)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showImport && <ProspectImportModal onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); fetchRows(); refreshMeta(); }} />}

      {/* Convert to Buyer confirmation modal (with Send setup email toggle) */}
      {convertBuyerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConvertBuyerModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0A0A0A] border border-white/10" data-testid="convert-buyer-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-lg font-semibold">Convert to Buyer</h2>
              <button onClick={() => setConvertBuyerModal(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-white/70">
                Create a Buyer account for <span className="text-white font-semibold">{convertBuyerModal.prospect.company_name}</span>?
              </div>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-white/10 hover:bg-white/[0.03]">
                <input
                  type="checkbox"
                  checked={convertBuyerModal.sendEmail}
                  onChange={(e) => setConvertBuyerModal((m) => ({ ...m, sendEmail: e.target.checked }))}
                  data-testid="convert-buyer-send-email"
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm text-white font-medium">Send setup email now</div>
                  <div className="text-xs text-white/50 mt-0.5">
                    {convertBuyerModal.sendEmail
                      ? "Contact will receive a password-setup link valid for 24 hours."
                      : "No email will be sent. You can manually trigger one later."}
                  </div>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5">
              <button onClick={() => setConvertBuyerModal(null)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button
                onClick={() => runConvertToBuyer(convertBuyerModal.prospect, convertBuyerModal.sendEmail)}
                disabled={busyId === convertBuyerModal.prospect.id}
                data-testid="convert-buyer-confirm-btn"
                className="px-4 py-2 bg-emerald-500 text-black font-semibold text-sm disabled:opacity-50"
              >
                {busyId === convertBuyerModal.prospect.id ? 'Converting…' : 'Convert to Buyer'}
              </button>
            </div>
          </div>
        </div>
      )}
      {editing !== null && (
        <ProspectEditModal prospect={editing.id ? editing : null} statuses={STATUSES_ORDERED}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchRows(); refreshMeta(); }} />
      )}
      {showDeleteAll && (
        <DangerConfirmModal
          open={showDeleteAll}
          onClose={() => setShowDeleteAll(false)}
          onConfirm={async ({ confirmation_phrase, acknowledged }) => {
            setDeleteAllLoading(true);
            try {
              const r = await deleteAllProspects(confirmation_phrase, acknowledged);
              toast.success(`Deleted ${r.data.deleted} prospects`);
              setShowDeleteAll(false);
              fetchRows();
              refreshMeta();
              setSelected(new Set());
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Delete failed');
            } finally { setDeleteAllLoading(false); }
          }}
          title="Delete All Prospects"
          description="This will permanently delete all prospects. Subscribers, campaigns, products, orders, sourcing requests, analytics, and users will not be affected."
          confirmPhrase="DELETE ALL PROSPECTS"
          loading={deleteAllLoading}
        />
      )}
      {bulkEnrich && (
        <BulkEnrichModal
          mode={bulkEnrich.mode}
          ids={bulkEnrich.ids}
          onClose={() => { setBulkEnrich(null); fetchRows(); refreshMeta(); }}
          onDone={() => { fetchRows(); refreshMeta(); }}
        />
      )}
      {sendEmailFor && (
        <SendProspectEmailModal
          prospect={sendEmailFor}
          onClose={() => {
            setSendEmailFor(null);
            // Phase 5.3 — if the composer was opened from the Hot
            // Leads inbox (deep-link), return there on close.
            if (composeOpenedFrom) {
              const target = composeOpenedFrom;
              setComposeOpenedFrom(null);
              navigate(target);
            }
          }}
          onSent={(updatedProspect) => {
            if (updatedProspect) {
              setRows((prev) => prev.map((r) => r.id === updatedProspect.id ? updatedProspect : r));
            }
            refreshMeta();
          }}
        />
      )}
      {bulkSendIds && (
        <BulkSendEmailModal
          ids={bulkSendIds}
          onClose={() => { setBulkSendIds(null); fetchRows(); refreshMeta(); }}
          onDone={() => { fetchRows(); refreshMeta(); }}
        />
      )}
    </div>
  );
};

export default AdminProspects;
